import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEventsV1Order from './events-v1-order-orders.http.js';
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

export interface Order {
  /** Unique order number. */
  orderNumber?: string;
  /**
   * Reservation ID.
   * @format GUID
   */
  reservationId?: string;
  /**
   * Payment snapshot ID. Empty for the `FREE` order.
   * @format GUID
   * @readonly
   */
  snapshotId?: string;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Contact ID of buyer, resolved using the email address specified. See the Contacts API for additional information.
   * @format GUID
   */
  contactId?: string;
  /**
   * Buyer member ID, if applicable.
   * @format GUID
   */
  memberId?: string;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /** Guest first name. */
  firstName?: string;
  /** Guest last name. */
  lastName?: string;
  /**
   * Guest email address.
   * @format EMAIL
   */
  email?: string;
  /** Checkout form response. When each purchased ticket is assigned to a guest, guest forms are returned for each ticket, and buyer info is returned. */
  checkoutForm?: FormResponse;
  /** Whether the order is confirmed. Triggered once the payment gateway processes the payment and funds reach the merchant's account. */
  confirmed?: boolean;
  /**
   * Order status. Possible values:
   * - `FREE`: The order is confirmed, no payment is required.
   * - `PENDING`: The order was paid, but the payment gateway suspended the payment.
   * - `PAID`: The order is paid.
   * - `OFFLINE_PENDING`: The order is confirmed but has to be paid in cash and the status is manually updated to `PAID`.
   * - `INITIATED`: The order is awaiting for payment.
   * - `CANCELED`: The order is canceled.
   * - `DECLINED`: The order is payment is declined.
   */
  status?: OrderStatusWithLiterals;
  /** Payment method used for purchase, for example, "payPal", "creditCard", etc. */
  method?: string;
  /** Quantity of ordered tickets. */
  ticketsQuantity?: number;
  /** Total order price. */
  totalPrice?: Money;
  /** Ticket PDF URL. */
  ticketsPdf?: string;
  /** Tickets (generated after payment). */
  tickets?: TicketingTicket[];
  /** Whether the order is archived. */
  archived?: boolean;
  /** Whether the order is anonymized by GDPR delete. */
  anonymized?: boolean;
  /** Guest full name. */
  fullName?: string;
  /** Order invoice. */
  invoice?: Invoice;
  /** Whether all tickets in an order are checked in. */
  fullyCheckedIn?: boolean;
  /** Internal order payment details. */
  paymentDetails?: PaymentDetails;
  /** Checkout channel type. */
  channel?: ChannelTypeWithLiterals;
  /**
   * Date and time the order was last updated.
   * @readonly
   */
  updated?: Date | null;
  /** Whether marketing consent was given. */
  marketingConsent?: boolean | null;
}

export interface FormResponse {
  /**
   * Input fields for a checkout form.
   * @maxSize 200
   */
  inputValues?: InputValue[];
}

export interface InputValue {
  /**
   * Input field name.
   * @maxLength 100
   */
  inputName?: string;
  /**
   * Input field value.
   * @maxLength 5000
   */
  value?: string;
  /**
   * Multiple input field values.
   * @maxSize 100
   * @maxLength 5000
   */
  values?: string[];
}

export interface FormattedAddress {
  /**
   * 1-line address representation.
   * @maxLength 200
   */
  formatted?: string;
  /** Address components. */
  address?: Address;
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

export enum OrderStatus {
  /** Order status isn't available for this request fieldset. */
  NA_ORDER_STATUS = 'NA_ORDER_STATUS',
  /** Order is confirmed, no payment is required. */
  FREE = 'FREE',
  /** Order was paid, but the payment gateway suspended the payment. */
  PENDING = 'PENDING',
  /** Order is paid. */
  PAID = 'PAID',
  /** Order is confirmed but must be paid via offline payment. Status needs to be manually updated to `"PAID"`. */
  OFFLINE_PENDING = 'OFFLINE_PENDING',
  /** Order is waiting for payment at the cashier. */
  INITIATED = 'INITIATED',
  /** Order is canceled. */
  CANCELED = 'CANCELED',
  /** Order payment is declined. */
  DECLINED = 'DECLINED',
  /** Order payment is authorized. */
  AUTHORIZED = 'AUTHORIZED',
  /** Order payment is voided. */
  VOIDED = 'VOIDED',
  /** Order is partially paid with less than the total amount. */
  PARTIALLY_PAID = 'PARTIALLY_PAID',
}

/** @enumType */
export type OrderStatusWithLiterals =
  | OrderStatus
  | 'NA_ORDER_STATUS'
  | 'FREE'
  | 'PENDING'
  | 'PAID'
  | 'OFFLINE_PENDING'
  | 'INITIATED'
  | 'CANCELED'
  | 'DECLINED'
  | 'AUTHORIZED'
  | 'VOIDED'
  | 'PARTIALLY_PAID';

export interface Money {
  /**
   * *Deprecated:** Use `value` instead.
   * @format DECIMAL_VALUE
   * @deprecated
   */
  amount?: string;
  /**
   * Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string | null;
}

export interface TicketingTicket {
  /** Unique ticket number which is assigned automatically when creating a ticket. */
  ticketNumber?: string;
  /** Associated order number. */
  orderNumber?: string;
  /**
   * Ticket definition ID. See the Ticket Definition API for additional information.
   * @format GUID
   */
  ticketDefinitionId?: string;
  /** Ticket type. */
  name?: string;
  /** Ticket price. */
  price?: Money;
  /**
   * Whether the ticket requires payment.
   * @readonly
   */
  free?: boolean;
  /** Event and ticket policies. */
  policy?: string;
  /** Ticket check-in. */
  checkIn?: CheckIn;
  /** Associated order status. */
  orderStatus?: OrderStatusWithLiterals;
  /**
   * Whether the order and ticket are archived.
   * If set to `true`, they aren't visible in the order list.
   */
  orderArchived?: boolean;
  /** Buyer full name. */
  orderFullName?: string;
  /** Guest full name. */
  guestFullName?: string | null;
  /** Guest personal details. */
  guestDetails?: GuestDetails;
  /** Whether the ticket is visible in an order. */
  archived?: boolean;
  /**
   * Ticket owner member ID.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Whether ticket was anonymized by GDPR delete.
   * Anonymized tickets no longer contain personally identifiable information (PII).
   */
  anonymized?: boolean;
  /**
   * Ticket check-in URL.
   * Shown as a QR code image in PDF.
   * Format: `https://www.wixevents.com/check-in/{ticket number},{event id}`.
   * Example: `https://www.wixevents.com/check-in/AAAA-AAAA-BB021,00000000-0000-0000-0000-000000000000`
   */
  checkInUrl?: string;
  /** URL for ticket PDF download. */
  ticketPdfUrl?: string;
  /** Associated order checkout channel type. */
  channel?: ChannelTypeWithLiterals;
  /**
   * URL to download ticket in the `.pkpass` format for Apple Wallet
   * @format WEB_URL
   * @readonly
   */
  walletPassUrl?: string;
  /**
   * Whether ticket is canceled.
   * @readonly
   */
  canceled?: boolean | null;
}

export interface CheckIn {
  /** Time of a ticket's check-in. */
  created?: Date | null;
}

export interface GuestDetails {
  /** Whether ticket belongs to assigned guest. */
  guestAssigned?: boolean;
  /** Guest first name. */
  firstName?: string | null;
  /** Guest last name. */
  lastName?: string | null;
  /**
   * Guest email.
   * @format EMAIL
   */
  email?: string | null;
  /** Full form response. */
  form?: FormResponse;
  /**
   * Contact ID associated with this guest.
   * @format GUID
   */
  contactId?: string | null;
  /**
   * Guest phone number.
   * @format PHONE
   */
  phone?: string | null;
}

export interface FormSubmission {
  /**
   * Form ID.
   * @readonly
   * @format GUID
   */
  formId?: string | null;
  /**
   * Form submission ID.
   * @readonly
   * @format GUID
   */
  submissionId?: string | null;
  /** Form submissions. */
  submissions?: Record<string, any>;
}

export enum ChannelType {
  /** Buyer created the order via an online channel, such as a website or mobile app. */
  ONLINE = 'ONLINE',
  /** Sales person created the order and collected the money. */
  OFFLINE_POS = 'OFFLINE_POS',
}

/** @enumType */
export type ChannelTypeWithLiterals = ChannelType | 'ONLINE' | 'OFFLINE_POS';

export interface TicketDetails {
  /**
   * Unique seat ID in the event venue.
   * @minLength 1
   * @maxLength 36
   */
  seatId?: string | null;
  /**
   * Section label.
   * @readonly
   */
  sectionLabel?: string | null;
  /**
   * Area label.
   * @readonly
   */
  areaLabel?: string | null;
  /**
   * Table label.
   * @readonly
   */
  tableLabel?: string | null;
  /**
   * Row label.
   * @readonly
   */
  rowLabel?: string | null;
  /**
   * Seat label in a row or table.
   * @readonly
   */
  seatLabel?: string | null;
  /**
   * Seating plan ID.
   * @format GUID
   * @readonly
   */
  planId?: string | null;
  /**
   * Number of places in the spot.
   *
   * Default: `1`.
   * @min 1
   * @max 50
   */
  capacity?: number | null;
  /**
   * Custom price of a ticket.
   * @format DECIMAL_VALUE
   */
  priceOverride?: string | null;
  /**
   * Pricing option ID.
   * @format GUID
   */
  pricingOptionId?: string | null;
  /**
   * Pricing option name.
   * @readonly
   */
  pricingOptionName?: string | null;
}

export interface Invoice {
  /** Items listed in the invoice. */
  items?: Item[];
  /**
   * Total cart amount.
   * @deprecated
   */
  total?: Money;
  /** Discount applied to a cart. */
  discount?: Discount;
  /** Tax applied to a cart. */
  tax?: Tax;
  /** Total cart amount before discount, tax, and fees. */
  subTotal?: Money;
  /**
   * Total amount of a cart after discount, tax, and fees.
   * Grand total is calculated in the following order:
   * 1. Total prices of all items in the cart are calculated.
   * 2. Discount is subtracted from the cart, if applicable.
   * 3. Tax is added, if applicable.
   * 4. Wix ticket service fee is added.
   */
  grandTotal?: Money;
  /**
   * Fees applied to the cart.
   * @readonly
   */
  fees?: Fee[];
  /** Total revenue, excluding fees. Taxes and payment provider fees aren't deducted. */
  revenue?: Money;
  /** Invoice preview URL. This value is only returned when the order is paid. */
  previewUrl?: string | null;
}

export interface Item {
  /**
   * Unique line item ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Line item quantity.
   * @min 1
   * @max 50
   */
  quantity?: number;
  /** Line item name. */
  name?: string;
  /** Line item price. */
  price?: Money;
  /** Total price for line items. It's calculated by multiplying price and item quantity. */
  total?: Money;
  /** Discount applied to the line item. */
  discount?: Discount;
  /** Tax applied to the item. */
  tax?: Tax;
  /**
   * Fees applied to the item.
   * @readonly
   */
  fees?: Fee[];
}

export interface Discount {
  /** Total discount amount. */
  amount?: Money;
  /** Total sum after the discount. */
  afterDiscount?: Money;
  /**
   * Discount coupon code.
   * @deprecated
   */
  code?: string;
  /**
   * Discount coupon name.
   * @deprecated
   */
  name?: string;
  /**
   * Discount coupon ID.
   * @deprecated
   */
  couponId?: string;
  /** Discount items. */
  discounts?: DiscountItem[];
}

export interface DiscountItem extends DiscountItemDiscountOneOf {
  /** Coupon discount. */
  coupon?: CouponDiscount;
  /** Pricing plan discount. */
  paidPlan?: PaidPlanDiscount;
  /** Total discount amount. */
  amount?: Money;
}

/** @oneof */
export interface DiscountItemDiscountOneOf {
  /** Coupon discount. */
  coupon?: CouponDiscount;
  /** Pricing plan discount. */
  paidPlan?: PaidPlanDiscount;
}

export interface CouponDiscount {
  /** Discount coupon name. **Deprecated:** Use `invoice.discounts.coupon.name` instead. */
  name?: string;
  /** Discount coupon code. **Deprecated:** Use `invoice.discounts.coupon.code` instead. */
  code?: string;
  /** Discount coupon ID. **Deprecated:** Use `invoice.discounts.coupon.couponId` instead. */
  couponId?: string;
}

export interface PaidPlanDiscount extends PaidPlanDiscountDiscountOneOf {
  /** Discount by percentage applied to tickets. */
  percentDiscount?: PercentDiscount;
  /** Name of pricing plan. */
  name?: string;
}

/** @oneof */
export interface PaidPlanDiscountDiscountOneOf {
  /** Discount by percentage applied to tickets. */
  percentDiscount?: PercentDiscount;
}

export interface PercentDiscount {
  /**
   * Percent rate.
   * @decimalValue options { gte:0.01, lte:100, maxScale:2 }
   */
  rate?: string;
  /** Number of discounted tickets. */
  quantityDiscounted?: number;
}

export interface Tax {
  /** Tax type. */
  type?: TaxTypeWithLiterals;
  /**
   * Tax name.
   * @readonly
   */
  name?: string;
  /**
   * Tax rate.
   * @format DECIMAL_VALUE
   */
  rate?: string;
  /** Taxable amount. */
  taxable?: Money;
  /** Total tax amount. */
  amount?: Money;
}

export enum TaxType {
  /** Tax is included in the ticket price. */
  INCLUDED = 'INCLUDED',
  /** Tax is added to the order at the checkout. */
  ADDED = 'ADDED',
  /** Tax is added to the final total at the checkout. */
  ADDED_AT_CHECKOUT = 'ADDED_AT_CHECKOUT',
}

/** @enumType */
export type TaxTypeWithLiterals =
  | TaxType
  | 'INCLUDED'
  | 'ADDED'
  | 'ADDED_AT_CHECKOUT';

export interface Fee {
  /** Fee identifier. */
  name?: FeeNameWithLiterals;
  /** How fee is calculated. */
  type?: FeeTypeWithLiterals;
  /**
   * Fee rate.
   * @format DECIMAL_VALUE
   * @readonly
   */
  rate?: string;
  /** Total amount of fee charges. */
  amount?: Money;
}

export enum FeeName {
  /** Wix ticket service fee charges applied to the line item. */
  WIX_FEE = 'WIX_FEE',
}

/** @enumType */
export type FeeNameWithLiterals = FeeName | 'WIX_FEE';

export enum FeeType {
  /** Fee is added to the ticket price at checkout. */
  FEE_ADDED = 'FEE_ADDED',
  /** Seller absorbs the fee. It's deducted from the ticket price. */
  FEE_INCLUDED = 'FEE_INCLUDED',
  /** Fee is added to the ticket price at checkout. */
  FEE_ADDED_AT_CHECKOUT = 'FEE_ADDED_AT_CHECKOUT',
}

/** @enumType */
export type FeeTypeWithLiterals =
  | FeeType
  | 'FEE_ADDED'
  | 'FEE_INCLUDED'
  | 'FEE_ADDED_AT_CHECKOUT';

export interface PaymentDetails {
  /** Wix Payments transaction. */
  transaction?: PaymentTransaction;
}

export interface PaymentTransaction {
  /**
   * Wix Payments transaction ID.
   * @format GUID
   * @readonly
   */
  transactionId?: string;
  /**
   * Transaction payment method, for example, "payPal", "creditCard", etc.
   * @readonly
   */
  method?: string;
}

export enum ScheduledActionEnumAction {
  /** Action not scheduled. */
  UNKNOWN_ACTION = 'UNKNOWN_ACTION',
  /** Captured after the delay. */
  CAPTURE = 'CAPTURE',
  /** Void after the delay. */
  VOID = 'VOID',
}

/** @enumType */
export type ScheduledActionEnumActionWithLiterals =
  | ScheduledActionEnumAction
  | 'UNKNOWN_ACTION'
  | 'CAPTURE'
  | 'VOID';

export enum Action {
  /** Order can be archived. */
  ARCHIVE = 'ARCHIVE',
  /** Order can be unarchived. */
  UNARCHIVE = 'UNARCHIVE',
  /** Order can be confirmed. */
  CONFIRM = 'CONFIRM',
  /** Order can be captured. */
  CAPTURE = 'CAPTURE',
  /** Order can be voided. */
  VOID = 'VOID',
}

/** @enumType */
export type ActionWithLiterals =
  | Action
  | 'ARCHIVE'
  | 'UNARCHIVE'
  | 'CONFIRM'
  | 'CAPTURE'
  | 'VOID';

export interface GiftCardPaymentDetails {
  /**
   * Gift card payment ID.
   * @format GUID
   */
  giftCardPaymentId?: string | null;
  /**
   * ID of the app that created the gift card.
   * @format GUID
   */
  appId?: string | null;
  /** Whether the gift card payment is voided. */
  voided?: boolean | null;
  /** Amount */
  amount?: Money;
  /**
   * Obfuscated gift card code.
   * @maxLength 30
   */
  obfuscatedCode?: string | null;
  /**
   * Gift card code.
   * @maxLength 30
   */
  code?: string | null;
}

export interface BalanceSummary {
  /** Amount left to pay. */
  balance?: Money;
}

/** Triggered when an order is deleted. */
export interface OrderDeleted {
  /** Date and time the order was deleted. */
  timestamp?: Date | null;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /** Unique order number. */
  orderNumber?: string;
  /** Contact ID associated with the order. */
  contactId?: string;
  /**
   * Member ID associated with the order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /**
   * Date and time the order was updated.
   * @readonly
   */
  updated?: Date | null;
  /** Whether the order was anonymized by GDPR delete. */
  anonymized?: boolean;
  /** Order type. */
  orderType?: OrderTypeWithLiterals;
  /** Whether the event was triggered by GDPR delete request. */
  triggeredByAnonymizeRequest?: boolean;
  /** Tickets generated after payment. */
  tickets?: Ticket[];
}

export enum OrderType {
  /** Buyer form is used for all tickets. */
  UNASSIGNED_TICKETS = 'UNASSIGNED_TICKETS',
  /** Each order ticket has its own form. */
  ASSIGNED_TICKETS = 'ASSIGNED_TICKETS',
}

/** @enumType */
export type OrderTypeWithLiterals =
  | OrderType
  | 'UNASSIGNED_TICKETS'
  | 'ASSIGNED_TICKETS';

export interface Ticket {
  /** Unique issued ticket number. */
  ticketNumber?: string;
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string;
  /** Ticket check-in. */
  checkIn?: CheckIn;
  /** Ticket price. */
  price?: Money;
  /** Whether ticket is archived. */
  archived?: boolean;
  /** Guest first name. */
  firstName?: string | null;
  /** Guest last name. */
  lastName?: string | null;
  /** Guest email. */
  email?: string | null;
  /**
   * Contact ID associated with this ticket.
   * @format GUID
   */
  contactId?: string | null;
  /** Whether ticket is confirmed */
  confirmed?: boolean;
  /**
   * Member ID associated with this ticket.
   * @format GUID
   */
  memberId?: string | null;
  /** Ticket form response (only assigned tickets contain separate forms). */
  form?: FormResponse;
  /** Ticket name. */
  ticketName?: string;
  /** Anonymized tickets no longer contain personally identifiable information (PII). */
  anonymized?: boolean;
  /** URL and password to online conference */
  onlineConferencingLogin?: OnlineConferencingLogin;
  /**
   * Seat ID associated with this ticket.
   * @maxLength 36
   */
  seatId?: string | null;
  /** Whether ticket is canceled. */
  canceled?: boolean | null;
}

export interface OnlineConferencingLogin {
  /**
   * Link URL to the online conference.
   * @format WEB_URL
   * @readonly
   */
  link?: string;
  /**
   * Password for the online conference.
   * @readonly
   */
  password?: string | null;
}

export interface ListOrdersRequest {
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   * @max 400
   */
  limit?: number;
  /**
   * Predefined sets of fields to return.
   * - `TICKETS`: Returns `tickets`.
   * - `DETAILS`: Returns `reservationId`, `snapshotId`, `created`, `firstName`, `lastName`, `confirmed`, `status`, `method`, `ticketsQuantity`, `totalPrice`, `ticketsPdf`, `archived`, `fullName`.
   * - `FORM` : Returns `checkoutForm`.
   * - `INVOICE`: Returns `invoice`.
   *
   * Default: If `fieldset` is not included in the request,  `orderNumber`, `eventId`, `contactId`, `memberId`, `anonymized`, `fullyCheckedIn` are returned.
   *
   * @maxSize 20
   */
  fieldset?: OrderFieldsetWithLiterals[];
  /**
   * Order status. Possible values:
   * - `FREE`: The order is confirmed, no payment is required.
   * - `PENDING`: The order was paid, but the payment gateway suspended the payment.
   * - `PAID`: The order is paid.
   * - `OFFLINE_PENDING`: The order is confirmed but has to be paid in cash and the status is manually updated to `PAID`.
   * - `INITIATED`: The order is awaiting for payment.
   * - `CANCELED`: The order is canceled.
   * - `DECLINED`: The order is payment is declined.
   */
  status?: OrderStatusWithLiterals[];
  /**
   * Event ID to which the order belongs.
   * @format GUID
   * @maxSize 100
   */
  eventId?: string[];
  /**
   * Order number.
   * @maxLength 36
   * @maxSize 500
   */
  orderNumber?: string[];
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Field facets,
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Search filter. You can search `fullName`, `email` and `orderNumber`.
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator ID.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order.
   * Default: `created:asc`.
   * @maxLength 100
   */
  sort?: string;
  /** Order tag. */
  tag?: OrderTagWithLiterals[];
  /**
   * Guest contact IDs.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
}

export enum OrderFieldset {
  /** Returns `tickets`. */
  TICKETS = 'TICKETS',
  /**
   * Returns `archived`, `confirmed`, `created`, `firstName`, `fullName`,
   * `lastName`, `method`, `reservationId`, `snapshotId`,
   * `status`, `ticketsPdf`, `ticketsQuantity`, `totalPrice`.
   */
  DETAILS = 'DETAILS',
  /** Returns `checkoutForm`. */
  FORM = 'FORM',
  /** Returns `invoice`. */
  INVOICE = 'INVOICE',
}

/** @enumType */
export type OrderFieldsetWithLiterals =
  | OrderFieldset
  | 'TICKETS'
  | 'DETAILS'
  | 'FORM'
  | 'INVOICE';

export enum OrderTag {
  /** Return only confirmed orders. */
  CONFIRMED = 'CONFIRMED',
  /** Return only unconfirmed orders. */
  UNCONFIRMED = 'UNCONFIRMED',
  /** Return only member orders. */
  MEMBER = 'MEMBER',
  /** Return only archived orders. */
  ARCHIVED = 'ARCHIVED',
  /** Return only non archived orders. */
  NON_ARCHIVED = 'NON_ARCHIVED',
  /** Return only orders with all guests checked-in. */
  FULLY_CHECKED_IN = 'FULLY_CHECKED_IN',
  /** Return only orders with no guests checked-in. */
  NOT_FULLY_CHECKED_IN = 'NOT_FULLY_CHECKED_IN',
}

/** @enumType */
export type OrderTagWithLiterals =
  | OrderTag
  | 'CONFIRMED'
  | 'UNCONFIRMED'
  | 'MEMBER'
  | 'ARCHIVED'
  | 'NON_ARCHIVED'
  | 'FULLY_CHECKED_IN'
  | 'NOT_FULLY_CHECKED_IN';

export interface ListOrdersResponse {
  /** Total orders matching the given filters. */
  total?: number;
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   * @max 100
   */
  limit?: number;
  /** Orders. */
  orders?: Order[];
  /** Filter facets. */
  facets?: Record<string, FacetCounts>;
  /** Order data enriched facets. */
  orderFacets?: OrderFacets;
}

export interface FacetCounts {
  /** Facet counts aggregated per value. */
  counts?: Record<string, number>;
}

export interface OrderFacets {
  /** Filter facets. */
  facets?: Record<string, OrderFacetCounts>;
}

export interface OrderFacetCounts {
  /** Facet counts aggregated per value */
  counts?: Record<string, Counts>;
}

export interface Counts {
  /** Number or orders */
  count?: number;
  /** Number of tickets within orders */
  tickets?: number;
  /** Number of tickets with check-in */
  ticketsCheckIn?: number;
}

export interface GetOrderRequest {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber: string;
  /**
   * Predefined sets of fields to return.
   * - `TICKETS`: Returns `tickets`.
   * - `DETAILS`: Returns `reservationId`, `snapshotId`, `created`, `firstName`, `lastName`, `confirmed`, `status`, `method`, `ticketsQuantity`, `totalPrice`, `ticketsPdf`, `archived`, `fullName`.
   * - `FORM` : Returns `checkoutForm`.
   * - `INVOICE`: Returns `invoice`.
   *
   * Default: If `fieldset` is not included in the request,  `orderNumber`, `eventId`, `contactId`, `memberId`, `anonymized`, `fullyCheckedIn` are returned.
   *
   * @maxSize 20
   */
  fieldset?: OrderFieldsetWithLiterals[];
}

export interface GetOrderResponse {
  /** Requested order. */
  order?: Order;
  /** "Add to calendar" links. */
  calendarLinks?: CalendarLinks;
}

export interface CalendarLinks {
  /** "Add to Google calendar" URL. */
  google?: string;
  /** "Download ICS calendar file" URL. */
  ics?: string;
}

export interface UpdateOrderRequest {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber: string;
  /** Set of field paths to update. */
  fields?: string[];
  /** Checkout form. */
  checkoutForm?: FormResponse;
  /** Whether to archive the order. */
  archived?: boolean;
}

export interface UpdateOrderResponse {
  /** Updated order. */
  order?: Order;
}

/** Triggered when an order is updated. */
export interface OrderUpdated {
  /** Date and time the order was updated. */
  timestamp?: Date | null;
  /**
   * Site language when the order was initiated.
   * @format LANGUAGE
   */
  language?: string | null;
  /**
   * Locale in which the order was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /** Unique order number. */
  orderNumber?: string;
  /** Contact ID associated with the order. */
  contactId?: string;
  /**
   * Member ID associated with the order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /**
   * Date and time the order was updated.
   * @readonly
   */
  updated?: Date | null;
  /** Buyer first name. */
  firstName?: string;
  /** Buyer last name. */
  lastName?: string;
  /** Buyer email. */
  email?: string;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /** Whether order is confirmed - occurs once payment gateway processes the payment and funds reach merchant's account. */
  confirmed?: boolean;
  /**
   * Order status. Possible values:
   * - `FREE`: The order is confirmed, no payment is required.
   * - `PENDING`: The order was paid, but the payment gateway suspended the payment.
   * - `PAID`: The order is paid.
   * - `OFFLINE_PENDING`: The order is confirmed but has to be paid in cash and the status is manually updated to `PAID`.
   * - `INITIATED`: The order is awaiting for payment.
   * - `CANCELED`: The order is canceled.
   * - `DECLINED`: The order is payment is declined.
   */
  status?: OrderStatusWithLiterals;
  /** Payment method used for paid tickets purchase, i.e. "payPal", "creditCard", etc. */
  method?: string | null;
  /** Tickets generated after payment. */
  tickets?: Ticket[];
  /** Whether order was archived and excluded from results. */
  archived?: boolean;
  /** Whether event was triggered by GDPR delete request. */
  triggeredByAnonymizeRequest?: boolean;
}

export interface BulkUpdateOrdersRequest {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique order number.
   * @minLength 1
   * @maxLength 36
   * @minSize 1
   * @maxSize 100
   */
  orderNumber?: string[];
  /** Set of fields to update. */
  fields?: string[];
  /** Whether to archive the order. */
  archived?: boolean;
}

export interface BulkUpdateOrdersResponse {
  /** Updated orders. */
  orders?: Order[];
}

export interface ConfirmOrderRequest {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Order numbers.
   * @minLength 1
   * @maxLength 36
   * @maxSize 100
   */
  orderNumber?: string[];
}

export interface ConfirmOrderResponse {
  /** Confirmed orders. */
  orders?: Order[];
}

export interface GetSummaryRequest {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string | null;
}

export interface GetSummaryResponse {
  /** Ticket sales grouped by currency. */
  sales?: TicketSales[];
}

export interface TicketSales {
  /** Total balance of confirmed transactions. */
  total?: Money;
  /** Total number of confirmed orders. */
  totalOrders?: number;
  /** Total number of tickets purchased. */
  totalTickets?: number;
  /** Total revenue, excluding fees (taxes and payment provider fees are not deducted). */
  revenue?: Money;
}

export interface GetInvoicePreviewRequest {
  /**
   * Event ID to which the invoice belongs.
   * @format GUID
   */
  eventId?: string;
  /** Order number. */
  orderNumber?: string;
}

export interface RawHttpResponse {
  body?: Uint8Array;
  statusCode?: number | null;
  headers?: HeadersEntry[];
}

export interface HeadersEntry {
  key?: string;
  value?: string;
}

export interface GetPaymentInfoRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Order number. */
  orderNumber?: string;
}

export interface GetPaymentInfoResponse {
  transactions?: PaymentTransactionSummary[];
  status?: string | null;
  /**
   * @format GUID
   * @readonly
   */
  transactionId?: string | null;
  /** Whether at least one transaction that is not in the final status exists. */
  active?: boolean;
}

export interface PaymentTransactionSummary {
  /**
   * Wix Payments transaction ID.
   * @format GUID
   * @readonly
   */
  transactionId?: string;
  /**
   * Final transaction status.
   * @readonly
   */
  finalTransactionStatus?: string;
  /** Transaction events. */
  events?: PaymentTransactionEvent[];
}

export interface PaymentTransactionEvent {
  /**
   * Order snapshot ID.
   * @format GUID
   * @readonly
   */
  snapshotId?: string;
  /**
   * Transaction status
   * @readonly
   */
  transactionStatus?: string;
  /**
   * Transaction Payment method e.g., "payPal", "creditCard", etc.
   * @readonly
   */
  paymentMethod?: string;
  /**
   * Transaction payment amount
   * @readonly
   */
  paymentAmount?: Money;
  /**
   * Crated date
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Reason code
   * @readonly
   */
  reasonCode?: string | null;
  /**
   * Refunded amount
   * @readonly
   */
  refundedAmount?: Money;
}

export interface CaptureAuthorizedPaymentRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * Order number.
   * @minLength 1
   * @maxLength 36
   */
  orderNumber: string;
}

export interface CaptureAuthorizedPaymentResponse {}

export interface VoidAuthorizedPaymentRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * Order number.
   * @minLength 1
   * @maxLength 36
   */
  orderNumber: string;
}

export interface VoidAuthorizedPaymentResponse {}

export interface FindOrderByReservationIdRequest {
  /**
   * Event ID
   * @format GUID
   */
  eventId?: string;
  /**
   * Reservation ID.
   * @format GUID
   */
  reservationId?: string;
}

export interface FindOrderByReservationIdResponse {
  /** Order. */
  order?: Order;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** Triggered when an order is confirmed. */
export interface OrderConfirmed {
  /** Date and time the order was confirmed. */
  timestamp?: Date | null;
  /**
   * Site language when the order was initiated.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
  /**
   * Locale in which the order was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /** Unique order number. */
  orderNumber?: string;
  /** Contact ID associated with the order. */
  contactId?: string;
  /**
   * Member ID associated with the order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /**
   * Date and time the order was updated.
   * @readonly
   */
  updated?: Date | null;
  /** Buyer first name. */
  firstName?: string;
  /** Buyer last name. */
  lastName?: string;
  /** Buyer email address. */
  email?: string;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /**
   * Order status. Possible values:
   * - `FREE`: The order is confirmed, no payment is required.
   * - `PENDING`: The order was paid, but the payment gateway suspended the payment.
   * - `PAID`: The order is paid.
   * - `OFFLINE_PENDING`: The order is confirmed but has to be paid in cash and the status is manually updated to `PAID`.
   * - `INITIATED`: The order is awaiting for payment.
   * - `CANCELED`: The order is canceled.
   * - `DECLINED`: The order is payment is declined.
   */
  status?: OrderStatusWithLiterals;
  /** Payment method used for paid tickets purchase, i.e. "payPal", "creditCard", etc. */
  method?: string | null;
  /** Tickets (generated after payment). */
  tickets?: Ticket[];
  /** Invoice. */
  invoice?: Invoice;
  /** Reservation ID associated with the order. */
  reservationId?: string;
}

/** Triggered when an order is paid. */
export interface OrderPaid {
  /** Date and time the order was paid. */
  timestamp?: Date | null;
  /**
   * Site language when the order was initiated.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
  /**
   * Locale in which the order was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /** Unique order number. */
  orderNumber?: string;
  /** Reservation ID associated with this order. */
  reservationId?: string;
  /**
   * Contact ID associated with this order.
   * @maxLength 36
   */
  contactId?: string;
  /**
   * Member ID associated with this order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /**
   * Buyer first name.
   * @maxLength 255
   */
  firstName?: string;
  /**
   * Buyer last name.
   * @maxLength 255
   */
  lastName?: string;
  /**
   * Buyer email address.
   * @maxLength 255
   */
  email?: string;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /** Order status. */
  status?: OrderStatusWithLiterals;
  /**
   * Payment method used for paid tickets purchase, i.e. "payPal", "creditCard", etc.
   * @maxLength 255
   */
  method?: string | null;
  /**
   * Tickets (generated after payment).
   * @maxSize 50
   */
  tickets?: Ticket[];
  /** Invoice. */
  invoice?: Invoice;
}

/** Triggered when a reservation is created. */
export interface ReservationCreated {
  /** Date and time the reservation was created. */
  timestamp?: Date | null;
  /**
   * Event ID to which the reservation belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Reservation ID.
   * Can be used to retrieve a reservation invoice.
   * @format GUID
   */
  reservationId?: string;
  /** Date and time the reservation expires. */
  expires?: Date | null;
  /** Reservation status. */
  status?: ReservationStatusWithLiterals;
  /**
   * Amount of tickets in the reservation.
   * @maxSize 50
   */
  quantities?: TicketQuantity[];
  /** Date and time the reservation was updated. */
  _updatedDate?: Date | null;
  /**
   * Number of reservations.
   * @maxSize 50
   */
  counts?: ReservationCount[];
}

export enum ReservationStatus {
  /** The reservation is pending confirmation. It will expire after a certain amount of time. */
  RESERVATION_PENDING = 'RESERVATION_PENDING',
  /** The reservation is confirmed and won't expire. */
  RESERVATION_CONFIRMED = 'RESERVATION_CONFIRMED',
  /** The reservation is canceled because it's not paid. */
  RESERVATION_CANCELED = 'RESERVATION_CANCELED',
  /** The reservation is canceled manually by the buyer. */
  RESERVATION_CANCELED_MANUALLY = 'RESERVATION_CANCELED_MANUALLY',
  /** The reservation has expired. */
  RESERVATION_EXPIRED = 'RESERVATION_EXPIRED',
}

/** @enumType */
export type ReservationStatusWithLiterals =
  | ReservationStatus
  | 'RESERVATION_PENDING'
  | 'RESERVATION_CONFIRMED'
  | 'RESERVATION_CANCELED'
  | 'RESERVATION_CANCELED_MANUALLY'
  | 'RESERVATION_EXPIRED';

export interface TicketQuantity {
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string | null;
  /** Quantity. */
  quantity?: number | null;
  /** Quantity update timestamp. */
  _updatedDate?: Date | null;
}

export interface ReservationCount {
  /** Reservation Count snapshot timestamp. */
  timestamp?: Date | null;
  /**
   * Ticket Definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string;
  /** Confirmed reservation count. */
  confirmedCount?: number;
  /** Pending reservation count. */
  pendingCount?: number;
  /** True if paid ticket reservation exist. */
  paidExists?: boolean;
}

/** Triggered when a reservation is updated. */
export interface ReservationUpdated {
  /** Date and time the reservation was updated. */
  timestamp?: Date | null;
  /**
   * Event ID to which the reservation belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Reservation ID.
   * Can be used to retrieve a reservation invoice.
   * @format GUID
   */
  reservationId?: string;
  /** Reservation status. */
  status?: ReservationStatusWithLiterals;
  /** Date and time the reservation expires. */
  expires?: Date | null;
  /**
   * Amount of tickets in the reservation.
   * @maxSize 50
   */
  quantities?: TicketQuantity[];
  /** Date and time the reservation was updated. */
  _updatedDate?: Date | null;
  /**
   * Number of reservations.
   * @maxSize 50
   */
  counts?: ReservationCount[];
}

export interface GetCheckoutOptionsRequest {}

export interface GetCheckoutOptionsResponse {
  /** Whether any payment method is configured and available for payment. */
  paymentMethodConfigured?: boolean;
  /** Whether coupons are accepted at checkout. */
  acceptCoupons?: boolean;
  /** Whether premium services are enabled. Enabled for free if site doesn't sell any paid tickets. Selling tickets for a fee requires a premium feature `"events_sell_tickets"`. */
  premiumServices?: boolean;
  /** Whether there are any paid tickets available for sale. */
  paidTickets?: boolean;
  /** Whether gift cards are accepted at checkout. */
  acceptGiftCards?: boolean;
}

export interface ListAvailableTicketsRequest {
  /**
   * Event ID to list tickets for. If not specified, available tickets for all events on a site will be returned.
   * @format GUID
   */
  eventId?: string;
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   *
   * **Default:** `0`. If you don't pass this field in the request, only `metadata` is returned.
   * @max 100
   */
  limit?: number;
  /**
   * Sort order.
   * Default: `created:asc`.
   * @maxLength 100
   */
  sort?: string;
  state?: StateWithLiterals[];
}

export enum State {
  INCLUDE_HIDDEN_NOT_ON_SALE = 'INCLUDE_HIDDEN_NOT_ON_SALE',
}

/** @enumType */
export type StateWithLiterals = State | 'INCLUDE_HIDDEN_NOT_ON_SALE';

export interface ListAvailableTicketsResponse {
  /** Ticket definitions meta data. */
  metaData?: ResponseMetaData;
  /** Ticket definitions. */
  definitions?: TicketDefinition[];
}

export interface ResponseMetaData {
  /** Number of items in the response. */
  count?: number;
  /** Offset of items. */
  offset?: number;
  /** Total number of matching items. */
  total?: number;
}

export interface TicketDefinition {
  /**
   * Ticket definition ID.
   * @format GUID
   */
  _id?: string;
  /** Ticket price. */
  price?: Money;
  /** Whether the ticket is free (read only). */
  free?: boolean;
  /** Ticket name. */
  name?: string;
  /** Ticket description. */
  description?: string;
  /**
   * Limit of tickets that can be purchased per checkout. If tickets are unlimited in the definition, the limit per checkout is 20 tickets.
   * @max 20
   */
  limitPerCheckout?: number;
  /** Custom sort index. */
  orderIndex?: number;
  /** Event and ticket policies. */
  policy?: string;
  /** Sensitive dashboard data. */
  dashboard?: Dashboard;
  /**
   * Event ID associated with the ticket.
   * @format GUID
   */
  eventId?: string;
  /**
   * Configuration of the fixed-rate Wix service fee that is applied at checkout to each ticket sold.
   * @readonly
   */
  wixFeeConfig?: WixFeeConfig;
  /** Ticket sale period. */
  salePeriod?: TicketSalePeriod;
  /**
   * Ticket sale status.
   * @readonly
   */
  saleStatus?: TicketSaleStatusWithLiterals;
  /** Ticket state. */
  state?: StateWithLiterals[];
  /** Ticket pricing. */
  pricing?: TicketPricing;
}

export interface Dashboard {
  /** Whether ticket is hidden and cannot be sold. */
  hidden?: boolean;
  /**
   * Number of tickets sold and reserved.
   * @deprecated
   */
  sold?: number;
  /** Whether the ticket has limited quantity. */
  limited?: boolean;
  /** Ticket limit. `NULL` if the tickets are unlimited. */
  quantity?: number | null;
  /** Number of unsold tickets. `NULL` if the tickets are unlimited. */
  unsold?: number | null;
  /** Number of tickets sold. */
  ticketsSold?: number;
  /** Number of tickets reserved. */
  ticketsReserved?: number;
}

export interface WixFeeConfig {
  /** Fee calculation method. */
  type?: FeeTypeWithLiterals;
}

export interface TicketSalePeriod {
  /** Ticket sale start timestamp. */
  startDate?: Date | null;
  /** Ticket sale end timestamp. */
  endDate?: Date | null;
  /** Whether to hide this ticket if it isn't on sale. */
  hideNotOnSale?: boolean;
}

export enum TicketSaleStatus {
  /** Ticket sale is scheduled to start. */
  SALE_SCHEDULED = 'SALE_SCHEDULED',
  /** Ticket sale has started. */
  SALE_STARTED = 'SALE_STARTED',
  /** Ticket sale has ended. */
  SALE_ENDED = 'SALE_ENDED',
}

/** @enumType */
export type TicketSaleStatusWithLiterals =
  | TicketSaleStatus
  | 'SALE_SCHEDULED'
  | 'SALE_STARTED'
  | 'SALE_ENDED';

export interface TicketPricing extends TicketPricingPriceOneOf {
  /** Ticket price which is read only. */
  fixedPrice?: Money;
  /** Min price per ticket, customizable. */
  minPrice?: Money;
  /** Ticket pricing options. */
  pricingOptions?: PricingOptions;
  /**
   * Ticket pricing type.
   * @readonly
   */
  pricingType?: TypeWithLiterals;
}

/** @oneof */
export interface TicketPricingPriceOneOf {
  /** Ticket price which is read only. */
  fixedPrice?: Money;
  /** Min price per ticket, customizable. */
  minPrice?: Money;
  /** Ticket pricing options. */
  pricingOptions?: PricingOptions;
}

export interface PricingOptions {
  /** Multiple ticket pricing options. */
  options?: PricingOption[];
}

export interface PricingOption {
  /**
   * Ticket pricing option ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Ticket pricing option name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /** Ticket pricing option price. */
  price?: Money;
}

export enum Type {
  STANDARD = 'STANDARD',
  DONATION = 'DONATION',
}

/** @enumType */
export type TypeWithLiterals = Type | 'STANDARD' | 'DONATION';

export interface QueryAvailableTicketsRequest {
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   *
   * **Default:** `0`. If you don't pass this field in the request, only `metadata` is returned.
   * @max 1000
   */
  limit?: number;
  /** Ticket definition. */
  filter?: Record<string, any> | null;
  /**
   * Which fields to return.
   * @maxSize 20
   */
  fieldset?: TicketDefinitionFieldsetWithLiterals[];
  /**
   * Sort order.
   * Default: `created:asc`.
   * @maxLength 100
   */
  sort?: string;
}

export enum TicketDefinitionFieldset {
  /** Include `policy` in the response. */
  POLICY = 'POLICY',
  /** Include `dashboard` in the response. */
  DASHBOARD = 'DASHBOARD',
}

/** @enumType */
export type TicketDefinitionFieldsetWithLiterals =
  | TicketDefinitionFieldset
  | 'POLICY'
  | 'DASHBOARD';

export interface QueryAvailableTicketsResponse {
  /** Ticket definitions meta data. */
  metaData?: ResponseMetaData;
  /** Ticket definitions. */
  definitions?: TicketDefinition[];
}

export interface CreateReservationRequest {
  /**
   * Event ID to which the reservation belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Tickets to reserve.
   * @minSize 1
   * @maxSize 50
   */
  ticketQuantities?: TicketReservationQuantity[];
  /** Whether to ignore the available ticket limits upon a reservation. */
  ignoreLimits?: boolean;
  /** Whether to allow reservations for hidden tickets. */
  allowHiddenTickets?: boolean;
}

export interface TicketReservationQuantity {
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string;
  /**
   * Quantity of tickets to reserve.
   * @min 1
   * @max 50
   */
  quantity?: number;
  /**
   * Override the predefined ticket price.
   * @maxLength 13
   * @format DECIMAL_VALUE
   */
  priceOverride?: string | null;
  /** Optional ticket details. */
  ticketDetails?: TicketDetails[];
}

export interface CreateReservationResponse {
  /**
   * Reservation ID.
   * @format GUID
   */
  _id?: string;
  /** Date and time the reservation expires. */
  expires?: Date | null;
  /** Ticket reservations. */
  reservations?: TicketReservation[];
  /** Reservation invoice. */
  invoice?: Invoice;
  /** Reservation status. */
  reservationStatus?: ReservationStatusWithLiterals;
}

export interface TicketReservation {
  /**
   * Amount of reserved tickets.
   * @min 1
   * @max 20
   */
  quantity?: number;
  /** An object containing ticket information. */
  ticket?: TicketDefinition;
  /** Optional ticket details. */
  ticketDetails?: TicketDetails[];
}

export interface CancelReservationRequest {
  /**
   * Event ID to which the reservation belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Reservation ID.
   * @format GUID
   */
  _id: string;
}

export interface CancelReservationResponse {}

export interface GetInvoiceRequest {
  /**
   * Event ID to which the invoice belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Reservation ID.
   * @format GUID
   */
  reservationId: string;
  /** Optional discount to be applied on the returned invoice. */
  withDiscount?: DiscountRequest;
  /** Optional benefit granted by the pricing plan to be applied on the returned invoice. */
  paidPlanBenefit?: PaidPlanBenefit;
}

export interface DiscountRequest {
  /**
   * Discount coupon code.
   * @maxLength 20
   */
  couponCode?: string;
}

export interface PaidPlanBenefit {
  /**
   * Pricing plan ID.
   * @format GUID
   */
  planOrderId?: string;
  /**
   * Pricing plan benefit ID.
   * @format GUID
   */
  benefitId?: string;
}

export interface GetInvoiceResponse {
  /** Invoice with applied discount. */
  invoice?: Invoice;
  /** Discount errors, if relevant. */
  discountErrors?: DiscountErrors;
  /** Reservation expiration time. */
  expires?: Date | null;
  /**
   * Reservation status. Possible values:
   * - `RESERVATION_PENDING`: The reservation is pending confirmation. It will expire after a certain amount of time.
   * - `RESERVATION_CONFIRMED`: The reservation is confirmed and will not expire.
   * - `RESERVATION_CANCELED`: The reservation is canceled because it's not paid.
   * - `RESERVATION_CANCELED_MANUALLY`: The reservation is canceled manually by the buyer.
   * - `RESERVATION_EXPIRED`: The reservation has expired.
   */
  reservationStatus?: ReservationStatusWithLiterals;
  /** Whether this reservation is already used in a checkout. */
  reservationOccupied?: boolean;
  /** Ticket reservations. */
  reservations?: TicketReservation[];
}

export interface DiscountErrors {
  /** Object containing error information. */
  error?: Error[];
}

export interface Error {
  /** A code identifying the error type. */
  code?: string;
}

export interface GiftCardErrors {
  /**
   * Error.
   * @maxSize 10
   */
  error?: GiftCardErrorsError[];
}

export interface GiftCardErrorsError {
  /** @maxLength 100 */
  code?: string;
}

export interface CheckoutRequest {
  /**
   * Event ID to which the checkout belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Ticket reservation ID.
   * @format GUID
   */
  reservationId?: string;
  /** Member ID. If empty, no site member is associated with this order. */
  memberId?: string;
  /** Discount to apply on the invoice. */
  discount?: DiscountRequest;
  /** Buyer details. */
  buyer?: Buyer;
  /**
   * Guest details.
   * @minSize 1
   * @maxSize 50
   */
  guests?: Guest[];
  /** Benefit granted by the pricing plan. */
  paidPlanBenefit?: PaidPlanBenefit;
  /** Options controlling the checkout process. */
  options?: CheckoutOptions;
  /** Whether marketing consent was given. */
  marketingConsent?: boolean | null;
}

export interface Buyer {
  /**
   * Buyer first name.
   * @minLength 1
   * @maxLength 50
   */
  firstName?: string;
  /**
   * Buyer last name.
   * @minLength 1
   * @maxLength 50
   */
  lastName?: string;
  /**
   * Buyer email.
   * @format EMAIL
   * @minLength 5
   * @maxLength 255
   */
  email?: string;
}

export interface Guest {
  /** Specific guest info. */
  form?: FormResponse;
}

export interface CheckoutOptions {
  /** Whether to ignore settings to notify contacts. */
  silent?: boolean;
  /** Whether the payment is to be done in person between the buyer and the merchant. When true, the completed order is created with status `OFFLINE_PENDING` and `inPerson` payment method. */
  payInPerson?: boolean;
  /** Whether to ignore form validation. */
  ignoreFormValidation?: boolean;
  /** Marks payment as already paid */
  markAsPaid?: boolean | null;
}

export interface CheckoutResponse {
  /** Created order. */
  order?: Order;
  /**
   * Order expiration time.
   * **Note:** Only applicable to orders with the `INITIATED` status.
   */
  expires?: Date | null;
  /** Ticket reservations. */
  reservations?: TicketReservation[];
  /** Order page URL. */
  orderPageUrl?: string | null;
}

/** Triggered when an order is initiated. */
export interface OrderInitiated {
  /** Date and time the order was initiated. */
  timestamp?: Date | null;
  /**
   * Site language when the order was initiated.
   * @format LANGUAGE
   */
  language?: string | null;
  /**
   * Locale in which the order was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /** Unique order number. */
  orderNumber?: string;
  /** Contact ID associated with the order. */
  contactId?: string;
  /**
   * Member ID associated with the order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /**
   * Date and time the order was updated.
   * @readonly
   */
  updated?: Date | null;
  /** Guest first name. */
  firstName?: string;
  /** Guest last name. */
  lastName?: string;
  /** Guest email address. */
  email?: string;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /**
   * Order status. Possible values:
   * - `FREE`: The order is confirmed, no payment is required.
   * - `PENDING`: The order was paid, but the payment gateway suspended the payment.
   * - `PAID`: The order is paid.
   * - `OFFLINE_PENDING`: The order is confirmed but has to be paid in cash and the status is manually updated to `PAID`.
   * - `INITIATED`: The order is awaiting for payment.
   * - `CANCELED`: The order is canceled.
   * - `DECLINED`: The order is payment is declined.
   */
  status?: OrderStatusWithLiterals;
  /** Invoice. */
  invoice?: Invoice;
  /** Reservation ID associated with the order. */
  reservationId?: string;
  /** Order was marked as paid. */
  markedAsPaid?: boolean | null;
  /** Whether marketing consent was given. */
  marketingConsent?: boolean | null;
}

export interface UpdateCheckoutRequest {
  /**
   * Event ID to which the checkout belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber: string;
  /** Buyer details. */
  buyer?: Buyer;
  /**
   * Guest details.
   * @minSize 1
   * @maxSize 20
   */
  guests?: Guest[];
  /**
   * Member ID.
   *
   * If empty, no site member is associated with this order.
   */
  memberId?: string | null;
  /** Discount to apply on the invoice. */
  discount?: DiscountRequest;
  /** Benefit granted by the pricing plan. */
  paidPlanBenefit?: PaidPlanBenefit;
}

export interface UpdateCheckoutResponse {
  /** Updated order. */
  order?: Order;
  /** Order page URL. */
  orderPageUrl?: string | null;
}

export interface OrderPageUrls {
  /**
   * Success order page URL.
   * @format WEB_URL
   */
  success?: string | null;
  /**
   * Pending order page URL.
   * @format WEB_URL
   */
  pending?: string | null;
  /**
   * Canceled order page URL.
   * @format WEB_URL
   */
  canceled?: string | null;
  /**
   * Error order page URL.
   * @format WEB_URL
   */
  error?: string | null;
}

export interface PosCheckoutRequest {
  /**
   * Event ID to which the checkout belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Ticket reservation ID.
   * @format GUID
   */
  reservationId: string;
  /**
   * Payment details ID.
   * Not required if reservation total is 0. In this case the order will be created with status `FREE` and no payment.
   * @format GUID
   */
  paymentDetailsId?: string | null;
}

export interface PosCheckoutResponse {
  /** Created order. */
  order?: Order;
  /** Time when the order expires, applies to orders with status is `INITIATED`. */
  expires?: Date | null;
  /** Ticket reservations. */
  reservations?: TicketReservation[];
}

export interface GetReservationRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * Ticket reservation ID.
   * @format GUID
   */
  reservationId?: string | null;
}

export interface GetReservationResponse {
  /** Reservation. */
  reservation?: Reservation;
}

export interface Reservation {
  /** Date and time the ticket reservation was created. */
  _createdDate?: Date | null;
  /** Date and time the ticket reservation was last updated. */
  _updatedDate?: Date | null;
  /** Date and time the pending ticket reservation will expire. */
  expirationDate?: Date | null;
  /** Reservation status. */
  reservationStatus?: ReservationStatusWithLiterals;
  /**
   * Ticket reservations.
   * @maxSize 50
   */
  reservations?: TicketReservation[];
}

export interface ConfirmReservationRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * Ticket reservation ID.
   * @format GUID
   */
  reservationId?: string | null;
}

export interface ConfirmReservationResponse {
  /** Reservation. */
  reservation?: Reservation;
}

export interface ExpireReservationRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * Ticket reservation ID.
   * @format GUID
   */
  reservationId?: string | null;
}

export interface ExpireReservationResponse {
  /** Reservation. */
  reservation?: Reservation;
}

export interface QueryEventsWithPaidReservationsRequest {
  /**
   * Event Keys.
   * @minSize 1
   * @maxSize 100
   */
  eventKeys?: EventKey[];
}

export interface EventKey {
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
}

export interface QueryEventsWithPaidReservationsResponse {
  /**
   * Event IDs.
   * @format GUID
   * @maxSize 100
   */
  eventIds?: string[] | null;
}

/** @docsIgnore */
export type UpdateOrderApplicationErrors = {
  code?: 'ORDER_ACTION_NOT_AVAILABLE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateOrdersApplicationErrors = {
  code?: 'ORDER_ACTION_NOT_AVAILABLE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type ConfirmOrderApplicationErrors = {
  code?: 'ORDER_ACTION_NOT_AVAILABLE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CaptureAuthorizedPaymentApplicationErrors = {
  code?: 'ORDER_STATUS_NOT_AUTHORIZED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type VoidAuthorizedPaymentApplicationErrors = {
  code?: 'ORDER_STATUS_NOT_AUTHORIZED';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface OrderDeletedEnvelope {
  data: OrderDeleted;
  metadata: BaseEventMetadata;
}

/**
 * This event is triggered when an order is deleted via a GDPR request.
 * @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Basic Events Order Info
 * @permissionScopeId SCOPE.DC-EVENTS.READ-BASIC-ORDERS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionId WIX_EVENTS.READ_ORDERS
 * @webhook
 * @eventType wix.events.ticketing.events.OrderDeleted
 * @serviceIdentifier wix.events.ticketing.OrderManagement
 * @slug order_deleted
 */
export declare function onOrderDeleted(
  handler: (event: OrderDeletedEnvelope) => void | Promise<void>
): void;

export interface OrderUpdatedEnvelope {
  data: OrderUpdated;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Basic Events Order Info
 * @permissionScopeId SCOPE.DC-EVENTS.READ-BASIC-ORDERS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionId WIX_EVENTS.READ_ORDERS
 * @webhook
 * @eventType wix.events.ticketing.events.OrderUpdated
 * @serviceIdentifier wix.events.ticketing.OrderManagement
 * @slug order_updated
 */
export declare function onOrderUpdated(
  handler: (event: OrderUpdatedEnvelope) => void | Promise<void>
): void;

export interface OrderConfirmedEnvelope {
  data: OrderConfirmed;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Basic Events Order Info
 * @permissionScopeId SCOPE.DC-EVENTS.READ-BASIC-ORDERS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionId WIX_EVENTS.READ_ORDERS
 * @webhook
 * @eventType wix.events.ticketing.events.OrderConfirmed
 * @serviceIdentifier wix.events.ticketing.CheckoutService
 * @slug order_confirmed
 */
export declare function onOrderConfirmed(
  handler: (event: OrderConfirmedEnvelope) => void | Promise<void>
): void;

export interface OrderInitiatedEnvelope {
  data: OrderInitiated;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Basic Events Order Info
 * @permissionScopeId SCOPE.DC-EVENTS.READ-BASIC-ORDERS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionId WIX_EVENTS.READ_ORDERS
 * @webhook
 * @eventType wix.events.ticketing.events.OrderInitiated
 * @serviceIdentifier wix.events.ticketing.CheckoutService
 * @slug order_initiated
 */
export declare function onOrderInitiated(
  handler: (event: OrderInitiatedEnvelope) => void | Promise<void>
): void;

export interface OrderReservationCreatedEnvelope {
  data: ReservationCreated;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Basic Events Order Info
 * @permissionScopeId SCOPE.DC-EVENTS.READ-BASIC-ORDERS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionId WIX_EVENTS.READ_INVOICE
 * @webhook
 * @eventType wix.events.ticketing.events.ReservationCreated
 * @serviceIdentifier wix.events.ticketing.CheckoutService
 * @slug reservation_created
 * @deprecated
 * @replacedBy wix.events.v1.ticket_reservation_created
 * @targetRemovalDate 2026-03-31
 */
export declare function onOrderReservationCreated(
  handler: (event: OrderReservationCreatedEnvelope) => void | Promise<void>
): void;

export interface OrderReservationUpdatedEnvelope {
  data: ReservationUpdated;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Basic Events Order Info
 * @permissionScopeId SCOPE.DC-EVENTS.READ-BASIC-ORDERS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionId WIX_EVENTS.READ_INVOICE
 * @webhook
 * @eventType wix.events.ticketing.events.ReservationUpdated
 * @serviceIdentifier wix.events.ticketing.CheckoutService
 * @slug reservation_updated
 * @deprecated
 * @replacedBy wix.events.v1.ticket_reservation_updated
 * @targetRemovalDate 2026-03-31
 */
export declare function onOrderReservationUpdated(
  handler: (event: OrderReservationUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a list of orders, including ticket data.
 * @public
 * @param options - An object representing the available options for retrieving a list of orders.
 * @permissionId WIX_EVENTS.READ_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.OrderManagement.ListOrders
 */
export async function listOrders(
  options?: ListOrdersOptions
): Promise<
  NonNullablePaths<
    ListOrdersResponse,
    | `total`
    | `offset`
    | `limit`
    | `orders`
    | `orders.${number}.orderNumber`
    | `orders.${number}.reservationId`
    | `orders.${number}.snapshotId`
    | `orders.${number}.eventId`
    | `orders.${number}.contactId`
    | `orders.${number}.memberId`
    | `orders.${number}.firstName`
    | `orders.${number}.lastName`
    | `orders.${number}.email`
    | `orders.${number}.confirmed`
    | `orders.${number}.status`
    | `orders.${number}.method`
    | `orders.${number}.ticketsQuantity`
    | `orders.${number}.totalPrice.amount`
    | `orders.${number}.totalPrice.currency`
    | `orders.${number}.ticketsPdf`
    | `orders.${number}.archived`
    | `orders.${number}.anonymized`
    | `orders.${number}.fullName`
    | `orders.${number}.invoice.discount.code`
    | `orders.${number}.invoice.discount.name`
    | `orders.${number}.invoice.discount.couponId`
    | `orders.${number}.invoice.tax.type`
    | `orders.${number}.invoice.tax.name`
    | `orders.${number}.invoice.tax.rate`
    | `orders.${number}.fullyCheckedIn`
    | `orders.${number}.paymentDetails.transaction.transactionId`
    | `orders.${number}.paymentDetails.transaction.method`
    | `orders.${number}.channel`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    offset: options?.offset,
    limit: options?.limit,
    fieldset: options?.fieldset,
    status: options?.status,
    eventId: options?.eventId,
    orderNumber: options?.orderNumber,
    memberId: options?.memberId,
    facet: options?.facet,
    searchPhrase: options?.searchPhrase,
    eventCreatorId: options?.eventCreatorId,
    sort: options?.sort,
    tag: options?.tag,
    contactId: options?.contactId,
  });

  const reqOpts = ambassadorWixEventsV1Order.listOrders(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'orders.checkoutForm.inputValues.address.address' },
            {
              path: 'orders.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          offset: '$[0].offset',
          limit: '$[0].limit',
          fieldset: '$[0].fieldset',
          status: '$[0].status',
          eventId: '$[0].eventId',
          orderNumber: '$[0].orderNumber',
          memberId: '$[0].memberId',
          facet: '$[0].facet',
          searchPhrase: '$[0].searchPhrase',
          eventCreatorId: '$[0].eventCreatorId',
          sort: '$[0].sort',
          tag: '$[0].tag',
          contactId: '$[0].contactId',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListOrdersOptions {
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   * @max 400
   */
  limit?: number;
  /**
   * Predefined sets of fields to return.
   * - `TICKETS`: Returns `tickets`.
   * - `DETAILS`: Returns `reservationId`, `snapshotId`, `created`, `firstName`, `lastName`, `confirmed`, `status`, `method`, `ticketsQuantity`, `totalPrice`, `ticketsPdf`, `archived`, `fullName`.
   * - `FORM` : Returns `checkoutForm`.
   * - `INVOICE`: Returns `invoice`.
   *
   * Default: If `fieldset` is not included in the request,  `orderNumber`, `eventId`, `contactId`, `memberId`, `anonymized`, `fullyCheckedIn` are returned.
   * @maxSize 20
   */
  fieldset?: OrderFieldsetWithLiterals[];
  /**
   * Order status. Possible values:
   * - `FREE`: The order is confirmed, no payment is required.
   * - `PENDING`: The order was paid, but the payment gateway suspended the payment.
   * - `PAID`: The order is paid.
   * - `OFFLINE_PENDING`: The order is confirmed but has to be paid in cash and the status is manually updated to `PAID`.
   * - `INITIATED`: The order is awaiting for payment.
   * - `CANCELED`: The order is canceled.
   * - `DECLINED`: The order is payment is declined.
   */
  status?: OrderStatusWithLiterals[];
  /**
   * Event ID to which the order belongs.
   * @format GUID
   * @maxSize 100
   */
  eventId?: string[];
  /**
   * Order number.
   * @maxLength 36
   * @maxSize 500
   */
  orderNumber?: string[];
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Field facets.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Search filter. You can search `fullName`, `email` and `orderNumber`.
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator ID.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order.
   * Default: `created:asc`.
   * @maxLength 100
   */
  sort?: string;
  /** Order tag. */
  tag?: OrderTagWithLiterals[];
  /**
   * Guest contact IDs.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
}

/**
 * Retrieves an order, including ticket data.
 * <!--
 * >The fieldsets in this function are restricted and only run if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 * @public
 * @requiredField identifiers
 * @requiredField identifiers.eventId
 * @requiredField identifiers.orderNumber
 * @param options - An object representing the available options for getting an order.
 * @param identifiers - An object containing identifiers for the order to be retrieved.
 * @permissionId WIX_EVENTS.READ_ORDERS
 * @applicableIdentity APP
 * @returns Requested order.
 * @fqn wix.events.ticketing.OrderManagement.GetOrder
 */
export async function getOrder(
  identifiers: NonNullablePaths<
    GetOrderIdentifiers,
    `eventId` | `orderNumber`,
    2
  >,
  options?: GetOrderOptions
): Promise<
  NonNullablePaths<
    Order,
    | `orderNumber`
    | `reservationId`
    | `snapshotId`
    | `eventId`
    | `contactId`
    | `memberId`
    | `firstName`
    | `lastName`
    | `email`
    | `checkoutForm.inputValues`
    | `checkoutForm.inputValues.${number}.inputName`
    | `checkoutForm.inputValues.${number}.value`
    | `confirmed`
    | `status`
    | `method`
    | `ticketsQuantity`
    | `totalPrice.amount`
    | `totalPrice.currency`
    | `ticketsPdf`
    | `tickets`
    | `tickets.${number}.ticketNumber`
    | `tickets.${number}.orderNumber`
    | `tickets.${number}.ticketDefinitionId`
    | `tickets.${number}.name`
    | `tickets.${number}.free`
    | `tickets.${number}.policy`
    | `tickets.${number}.orderStatus`
    | `tickets.${number}.orderArchived`
    | `tickets.${number}.orderFullName`
    | `tickets.${number}.guestDetails.guestAssigned`
    | `tickets.${number}.archived`
    | `tickets.${number}.anonymized`
    | `tickets.${number}.checkInUrl`
    | `tickets.${number}.ticketPdfUrl`
    | `tickets.${number}.channel`
    | `tickets.${number}.walletPassUrl`
    | `archived`
    | `anonymized`
    | `fullName`
    | `invoice.items`
    | `invoice.items.${number}._id`
    | `invoice.items.${number}.quantity`
    | `invoice.items.${number}.name`
    | `invoice.items.${number}.discount.code`
    | `invoice.items.${number}.discount.name`
    | `invoice.items.${number}.discount.couponId`
    | `invoice.items.${number}.tax.type`
    | `invoice.items.${number}.tax.name`
    | `invoice.items.${number}.tax.rate`
    | `invoice.fees`
    | `fullyCheckedIn`
    | `paymentDetails.transaction.transactionId`
    | `paymentDetails.transaction.method`
    | `channel`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: identifiers?.eventId,
    orderNumber: identifiers?.orderNumber,
    fieldset: options?.fieldset,
  });

  const reqOpts = ambassadorWixEventsV1Order.getOrder(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'order.checkoutForm.inputValues.address.address' },
            {
              path: 'order.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )?.order!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0].eventId',
          orderNumber: '$[0].orderNumber',
          fieldset: '$[1].fieldset',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetOrderIdentifiers {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber: string;
}

export interface GetOrderOptions {
  /**
   * Predefined sets of fields to return.
   * - `TICKETS`: Returns `tickets`.
   * - `DETAILS`: Returns `reservationId`, `snapshotId`, `created`, `firstName`, `lastName`, `confirmed`, `status`, `method`, `ticketsQuantity`, `totalPrice`, `ticketsPdf`, `archived`, `fullName`.
   * - `FORM` : Returns `checkoutForm`.
   * - `INVOICE`: Returns `invoice`.
   *
   * Default: If `fieldset` is not included in the request,  `orderNumber`, `eventId`, `contactId`, `memberId`, `anonymized`, `fullyCheckedIn` are returned.
   * @maxSize 20
   */
  fieldset?: OrderFieldsetWithLiterals[];
}

/**
 * Updates an order.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers.eventId
 * @requiredField identifiers.orderNumber
 * @param options - An object representing the available options for updating an order.
 * @param identifiers - An object containing identifiers for the order to be updated.
 * @permissionId WIX_EVENTS.MANAGE_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.OrderManagement.UpdateOrder
 */
export async function updateOrder(
  identifiers: NonNullablePaths<
    UpdateOrderIdentifiers,
    `eventId` | `orderNumber`,
    2
  >,
  options?: UpdateOrderOptions
): Promise<
  NonNullablePaths<
    UpdateOrderResponse,
    | `order.orderNumber`
    | `order.reservationId`
    | `order.snapshotId`
    | `order.eventId`
    | `order.contactId`
    | `order.memberId`
    | `order.firstName`
    | `order.lastName`
    | `order.email`
    | `order.checkoutForm.inputValues`
    | `order.checkoutForm.inputValues.${number}.inputName`
    | `order.checkoutForm.inputValues.${number}.value`
    | `order.confirmed`
    | `order.status`
    | `order.method`
    | `order.ticketsQuantity`
    | `order.totalPrice.amount`
    | `order.totalPrice.currency`
    | `order.ticketsPdf`
    | `order.tickets`
    | `order.tickets.${number}.ticketNumber`
    | `order.tickets.${number}.orderNumber`
    | `order.tickets.${number}.ticketDefinitionId`
    | `order.tickets.${number}.name`
    | `order.tickets.${number}.free`
    | `order.tickets.${number}.policy`
    | `order.tickets.${number}.orderStatus`
    | `order.tickets.${number}.orderArchived`
    | `order.tickets.${number}.orderFullName`
    | `order.tickets.${number}.guestDetails.guestAssigned`
    | `order.tickets.${number}.archived`
    | `order.tickets.${number}.anonymized`
    | `order.tickets.${number}.checkInUrl`
    | `order.tickets.${number}.ticketPdfUrl`
    | `order.tickets.${number}.channel`
    | `order.tickets.${number}.walletPassUrl`
    | `order.archived`
    | `order.anonymized`
    | `order.fullName`
    | `order.invoice.items`
    | `order.invoice.items.${number}._id`
    | `order.invoice.items.${number}.quantity`
    | `order.invoice.items.${number}.name`
    | `order.invoice.items.${number}.discount.code`
    | `order.invoice.items.${number}.discount.name`
    | `order.invoice.items.${number}.discount.couponId`
    | `order.invoice.items.${number}.tax.type`
    | `order.invoice.items.${number}.tax.name`
    | `order.invoice.items.${number}.tax.rate`
    | `order.invoice.fees`
    | `order.fullyCheckedIn`
    | `order.paymentDetails.transaction.transactionId`
    | `order.paymentDetails.transaction.method`
    | `order.channel`,
    7
  > & {
    __applicationErrorsType?: UpdateOrderApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      eventId: identifiers?.eventId,
      orderNumber: identifiers?.orderNumber,
      fields: options?.fields,
      checkoutForm: options?.checkoutForm,
      archived: options?.archived,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'checkoutForm.inputValues.address.address' }],
      },
    ]
  );

  const reqOpts = ambassadorWixEventsV1Order.updateOrder(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'order.checkoutForm.inputValues.address.address' },
            {
              path: 'order.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0].eventId',
          orderNumber: '$[0].orderNumber',
          fields: '$[1].fields',
          checkoutForm: '$[1].checkoutForm',
          archived: '$[1].archived',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateOrderIdentifiers {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber: string;
}

export interface UpdateOrderOptions {
  /** Set of field paths to update. */
  fields?: string[];
  /** Checkout form. */
  checkoutForm?: FormResponse;
  /** Whether to archive the order. */
  archived?: boolean;
}

/**
 * Archives multiple orders.
 * @public
 * @requiredField eventId
 * @param options - An object representing the available options for confirming an order.
 * @param eventId - Event ID to which the order belongs.
 * @permissionId WIX_EVENTS.MANAGE_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.OrderManagement.BulkUpdateOrders
 */
export async function bulkUpdateOrders(
  eventId: string,
  options?: BulkUpdateOrdersOptions
): Promise<
  NonNullablePaths<
    BulkUpdateOrdersResponse,
    | `orders`
    | `orders.${number}.orderNumber`
    | `orders.${number}.reservationId`
    | `orders.${number}.snapshotId`
    | `orders.${number}.eventId`
    | `orders.${number}.contactId`
    | `orders.${number}.memberId`
    | `orders.${number}.firstName`
    | `orders.${number}.lastName`
    | `orders.${number}.email`
    | `orders.${number}.confirmed`
    | `orders.${number}.status`
    | `orders.${number}.method`
    | `orders.${number}.ticketsQuantity`
    | `orders.${number}.totalPrice.amount`
    | `orders.${number}.totalPrice.currency`
    | `orders.${number}.ticketsPdf`
    | `orders.${number}.archived`
    | `orders.${number}.anonymized`
    | `orders.${number}.fullName`
    | `orders.${number}.invoice.discount.code`
    | `orders.${number}.invoice.discount.name`
    | `orders.${number}.invoice.discount.couponId`
    | `orders.${number}.invoice.tax.type`
    | `orders.${number}.invoice.tax.name`
    | `orders.${number}.invoice.tax.rate`
    | `orders.${number}.fullyCheckedIn`
    | `orders.${number}.paymentDetails.transaction.transactionId`
    | `orders.${number}.paymentDetails.transaction.method`
    | `orders.${number}.channel`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateOrdersApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    orderNumber: options?.orderNumber,
    fields: options?.fields,
    archived: options?.archived,
  });

  const reqOpts = ambassadorWixEventsV1Order.bulkUpdateOrders(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'orders.checkoutForm.inputValues.address.address' },
            {
              path: 'orders.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          orderNumber: '$[1].orderNumber',
          fields: '$[1].fields',
          archived: '$[1].archived',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateOrdersOptions {
  /**
   * Unique order number.
   * @minLength 1
   * @maxLength 36
   * @minSize 1
   * @maxSize 100
   */
  orderNumber?: string[];
  /** Set of fields to update. */
  fields?: string[];
  /** Whether to archive the order. */
  archived?: boolean;
}

/**
 * Confirms an order.
 *
 *
 * This function changes order status from `INITIATED`, `PENDING`, `OFFLINE_PENDING` to `PAID`.
 * Confirming orders with `INITIATED` or `PENDING` status triggers an email with the tickets to the buyer (and to additional guests, if provided).
 * @public
 * @requiredField eventId
 * @param options - An object representing the available options for confirming an order.
 * @param eventId - Event ID to which the order belongs.
 * @permissionId WIX_EVENTS.MANAGE_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.OrderManagement.ConfirmOrder
 */
export async function confirmOrder(
  eventId: string,
  options?: ConfirmOrderOptions
): Promise<
  NonNullablePaths<
    ConfirmOrderResponse,
    | `orders`
    | `orders.${number}.orderNumber`
    | `orders.${number}.reservationId`
    | `orders.${number}.snapshotId`
    | `orders.${number}.eventId`
    | `orders.${number}.contactId`
    | `orders.${number}.memberId`
    | `orders.${number}.firstName`
    | `orders.${number}.lastName`
    | `orders.${number}.email`
    | `orders.${number}.confirmed`
    | `orders.${number}.status`
    | `orders.${number}.method`
    | `orders.${number}.ticketsQuantity`
    | `orders.${number}.totalPrice.amount`
    | `orders.${number}.totalPrice.currency`
    | `orders.${number}.ticketsPdf`
    | `orders.${number}.archived`
    | `orders.${number}.anonymized`
    | `orders.${number}.fullName`
    | `orders.${number}.invoice.discount.code`
    | `orders.${number}.invoice.discount.name`
    | `orders.${number}.invoice.discount.couponId`
    | `orders.${number}.invoice.tax.type`
    | `orders.${number}.invoice.tax.name`
    | `orders.${number}.invoice.tax.rate`
    | `orders.${number}.fullyCheckedIn`
    | `orders.${number}.paymentDetails.transaction.transactionId`
    | `orders.${number}.paymentDetails.transaction.method`
    | `orders.${number}.channel`,
    6
  > & {
    __applicationErrorsType?: ConfirmOrderApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    orderNumber: options?.orderNumber,
  });

  const reqOpts = ambassadorWixEventsV1Order.confirmOrder(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'orders.checkoutForm.inputValues.address.address' },
            {
              path: 'orders.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          orderNumber: '$[1].orderNumber',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ConfirmOrderOptions {
  /**
   * Order numbers.
   * @minLength 1
   * @maxLength 36
   * @maxSize 100
   */
  orderNumber?: string[];
}

/**
 * Retrieves a summary of total ticket sales.
 * <!--
 * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 * @public
 * @param options - An object representing the available options for retrieving a summary of total ticket sales.
 * @permissionId WIX_EVENTS.READ_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.OrderManagement.GetSummary
 */
export async function getSummary(
  options?: GetSummaryOptions
): Promise<
  NonNullablePaths<
    GetSummaryResponse,
    | `sales`
    | `sales.${number}.total.amount`
    | `sales.${number}.total.currency`
    | `sales.${number}.totalOrders`
    | `sales.${number}.totalTickets`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: options?.eventId,
  });

  const reqOpts = ambassadorWixEventsV1Order.getSummary(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { eventId: '$[0].eventId' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetSummaryOptions {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string | null;
}

/**
 * Captures authorized payment asynchronously.
 * Eventually order will become paid.
 * For orders with non-authorized payments request will fail.
 * @param orderNumber - Order number.
 * @public
 * @requiredField orderNumber
 * @permissionId WIX_EVENTS.MANAGE_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.OrderManagement.CaptureAuthorizedPayment
 */
export async function captureAuthorizedPayment(
  orderNumber: string,
  options?: CaptureAuthorizedPaymentOptions
): Promise<
  void & {
    __applicationErrorsType?: CaptureAuthorizedPaymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    orderNumber: orderNumber,
    eventId: options?.eventId,
  });

  const reqOpts = ambassadorWixEventsV1Order.captureAuthorizedPayment(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          orderNumber: '$[0]',
          eventId: '$[1].eventId',
        },
        singleArgumentUnchanged: false,
      },
      ['orderNumber', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CaptureAuthorizedPaymentOptions {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
}

/**
 * Voids authorized payment asynchronously.
 * Eventually order will become voided.
 * For orders with non-authorized payments request will fail.
 * @param orderNumber - Order number.
 * @public
 * @requiredField orderNumber
 * @permissionId WIX_EVENTS.MANAGE_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.OrderManagement.VoidAuthorizedPayment
 */
export async function voidAuthorizedPayment(
  orderNumber: string,
  options?: VoidAuthorizedPaymentOptions
): Promise<
  void & {
    __applicationErrorsType?: VoidAuthorizedPaymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    orderNumber: orderNumber,
    eventId: options?.eventId,
  });

  const reqOpts = ambassadorWixEventsV1Order.voidAuthorizedPayment(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          orderNumber: '$[0]',
          eventId: '$[1].eventId',
        },
        singleArgumentUnchanged: false,
      },
      ['orderNumber', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface VoidAuthorizedPaymentOptions {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
}

/**
 * Retrieves checkout details.
 * @public
 * @permissionId WIX_EVENTS.READ_CHECKOUT
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.GetCheckoutOptions
 */
export async function getCheckoutOptions(): Promise<
  NonNullablePaths<
    GetCheckoutOptionsResponse,
    | `paymentMethodConfigured`
    | `acceptCoupons`
    | `premiumServices`
    | `paidTickets`
    | `acceptGiftCards`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixEventsV1Order.getCheckoutOptions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Returns tickets available to reserve.
 * <!--
 * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 * @public
 * @param options - An object representing the available options for retrieving a list of tickets available for reservation.
 * @permissionId WIX_EVENTS.READ_CHECKOUT
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.ListAvailableTickets
 */
export async function listAvailableTickets(
  options?: ListAvailableTicketsOptions
): Promise<
  NonNullablePaths<
    ListAvailableTicketsResponse,
    | `metaData.count`
    | `metaData.offset`
    | `metaData.total`
    | `definitions`
    | `definitions.${number}._id`
    | `definitions.${number}.price.amount`
    | `definitions.${number}.price.currency`
    | `definitions.${number}.free`
    | `definitions.${number}.name`
    | `definitions.${number}.description`
    | `definitions.${number}.limitPerCheckout`
    | `definitions.${number}.orderIndex`
    | `definitions.${number}.policy`
    | `definitions.${number}.dashboard.hidden`
    | `definitions.${number}.dashboard.sold`
    | `definitions.${number}.dashboard.limited`
    | `definitions.${number}.dashboard.ticketsSold`
    | `definitions.${number}.dashboard.ticketsReserved`
    | `definitions.${number}.eventId`
    | `definitions.${number}.wixFeeConfig.type`
    | `definitions.${number}.salePeriod.hideNotOnSale`
    | `definitions.${number}.saleStatus`
    | `definitions.${number}.pricing.pricingType`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: options?.eventId,
    offset: options?.offset,
    limit: options?.limit,
    sort: options?.sort,
    state: options?.state,
  });

  const reqOpts = ambassadorWixEventsV1Order.listAvailableTickets(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0].eventId',
          offset: '$[0].offset',
          limit: '$[0].limit',
          sort: '$[0].sort',
          state: '$[0].state',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListAvailableTicketsOptions {
  /**
   * Event ID to list tickets for. If not specified, available tickets for all events on a site will be returned.
   * @format GUID
   */
  eventId?: string;
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   *
   * **Default:** `0`. If you don't pass this field in the request, only `metadata` is returned.
   * @max 100
   */
  limit?: number;
  /**
   * Sort order.
   * Default: `created:asc`.
   * @maxLength 100
   */
  sort?: string;
  state?: StateWithLiterals[];
}

/**
 * Returns tickets available to reserve.
 * <!--
 * > Note: The fieldsets in this function are restricted and only run if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 * @public
 * @param options - An object representing the available options for retrieving a list of tickets available for reservation.
 * @permissionId WIX_EVENTS.READ_CHECKOUT
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.QueryAvailableTickets
 */
export async function queryAvailableTickets(
  options?: QueryAvailableTicketsOptions
): Promise<
  NonNullablePaths<
    QueryAvailableTicketsResponse,
    | `metaData.count`
    | `metaData.offset`
    | `metaData.total`
    | `definitions`
    | `definitions.${number}._id`
    | `definitions.${number}.price.amount`
    | `definitions.${number}.price.currency`
    | `definitions.${number}.free`
    | `definitions.${number}.name`
    | `definitions.${number}.description`
    | `definitions.${number}.limitPerCheckout`
    | `definitions.${number}.orderIndex`
    | `definitions.${number}.policy`
    | `definitions.${number}.dashboard.hidden`
    | `definitions.${number}.dashboard.sold`
    | `definitions.${number}.dashboard.limited`
    | `definitions.${number}.dashboard.ticketsSold`
    | `definitions.${number}.dashboard.ticketsReserved`
    | `definitions.${number}.eventId`
    | `definitions.${number}.wixFeeConfig.type`
    | `definitions.${number}.salePeriod.hideNotOnSale`
    | `definitions.${number}.saleStatus`
    | `definitions.${number}.pricing.pricingType`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    offset: options?.offset,
    limit: options?.limit,
    filter: options?.filter,
    fieldset: options?.fieldset,
    sort: options?.sort,
  });

  const reqOpts = ambassadorWixEventsV1Order.queryAvailableTickets(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          offset: '$[0].offset',
          limit: '$[0].limit',
          filter: '$[0].filter',
          fieldset: '$[0].fieldset',
          sort: '$[0].sort',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryAvailableTicketsOptions {
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   *
   * **Default:** `0`. If you don't pass this field in the request, only `metadata` is returned.
   * @max 1000
   */
  limit?: number;
  /** Ticket definition. */
  filter?: Record<string, any> | null;
  /**
   * Which fields to return.
   * @maxSize 20
   */
  fieldset?: TicketDefinitionFieldsetWithLiterals[];
  /**
   * Sort order.
   * Default: `created:asc`.
   * @maxLength 100
   */
  sort?: string;
}

/**
 * Reserves tickets for 20 minutes.
 *
 *
 * Reserved tickets are deducted from ticket stock and cannot be bought by another site visitor.
 * When the reservation expires, the tickets are added back to the stock.
 * @public
 * @requiredField eventId
 * @param options - An object representing the available options for creating a reservation.
 * @param eventId - Event ID to which the reservation belongs.
 * @permissionId WIX_EVENTS.CHECKOUT
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.CreateReservation
 * @deprecated
 * @replacedBy wix.events.ticketreservations.v1.TicketReservationManagement.CreateTicketReservation
 * @targetRemovalDate 2026-03-31
 */
export async function createReservation(
  eventId: string,
  options?: CreateReservationOptions
): Promise<
  NonNullablePaths<
    CreateReservationResponse,
    | `_id`
    | `reservations`
    | `reservations.${number}.quantity`
    | `reservations.${number}.ticket._id`
    | `reservations.${number}.ticket.price.amount`
    | `reservations.${number}.ticket.price.currency`
    | `reservations.${number}.ticket.free`
    | `reservations.${number}.ticket.name`
    | `reservations.${number}.ticket.description`
    | `reservations.${number}.ticket.limitPerCheckout`
    | `reservations.${number}.ticket.orderIndex`
    | `reservations.${number}.ticket.policy`
    | `reservations.${number}.ticket.dashboard.hidden`
    | `reservations.${number}.ticket.dashboard.sold`
    | `reservations.${number}.ticket.dashboard.limited`
    | `reservations.${number}.ticket.dashboard.ticketsSold`
    | `reservations.${number}.ticket.dashboard.ticketsReserved`
    | `reservations.${number}.ticket.eventId`
    | `reservations.${number}.ticket.wixFeeConfig.type`
    | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
    | `reservations.${number}.ticket.saleStatus`
    | `reservations.${number}.ticket.pricing.pricingType`
    | `invoice.items`
    | `invoice.items.${number}._id`
    | `invoice.items.${number}.quantity`
    | `invoice.items.${number}.name`
    | `invoice.items.${number}.price.amount`
    | `invoice.items.${number}.price.currency`
    | `invoice.items.${number}.discount.code`
    | `invoice.items.${number}.discount.name`
    | `invoice.items.${number}.discount.couponId`
    | `invoice.items.${number}.tax.type`
    | `invoice.items.${number}.tax.name`
    | `invoice.items.${number}.tax.rate`
    | `invoice.fees`
    | `reservationStatus`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    ticketQuantities: options?.ticketQuantities,
    ignoreLimits: options?.ignoreLimits,
    allowHiddenTickets: options?.allowHiddenTickets,
  });

  const reqOpts = ambassadorWixEventsV1Order.createReservation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          ticketQuantities: '$[1].ticketQuantities',
          ignoreLimits: '$[1].ignoreLimits',
          allowHiddenTickets: '$[1].allowHiddenTickets',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateReservationOptions {
  /**
   * Tickets to reserve.
   * @minSize 1
   * @maxSize 50
   */
  ticketQuantities?: TicketReservationQuantity[];
  /** Whether to ignore the available ticket limits upon a reservation. */
  ignoreLimits?: boolean;
  /** Whether to allow reservations for hidden tickets. */
  allowHiddenTickets?: boolean;
}

/**
 * Cancels ticket reservation and returns tickets to stock.
 * <!--
 * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 * @param _id - Reservation ID.
 * @public
 * @requiredField _id
 * @requiredField eventId
 * @param identifiers - An object containing identifiers for the reservation to be cancelled.
 * @param eventId - Event ID to which the reservation belongs.
 * @permissionId WIX_EVENTS.CHECKOUT
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.CancelReservation
 * @deprecated
 * @replacedBy wix.events.ticketreservations.v1.TicketReservationManagement.CancelTicketReservation
 * @targetRemovalDate 2026-03-31
 */
export async function cancelReservation(
  _id: string,
  eventId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    eventId: eventId,
  });

  const reqOpts = ambassadorWixEventsV1Order.cancelReservation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]', eventId: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'eventId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Generates a preview of an invoice, including the given coupon or pricing plan.
 * @param reservationId - Reservation ID.
 * @public
 * @requiredField eventId
 * @requiredField reservationId
 * @param options - An object representing the available options for generating a preview of a reservation invoice.
 * @param identifiers - An object containing identifiers for the reservation invoice preview to be generated.
 * @param eventId - Event ID to which the invoice belongs.
 * @permissionId WIX_EVENTS.READ_INVOICE
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.GetInvoice
 */
export async function getInvoice(
  reservationId: string,
  eventId: string,
  options?: GetInvoiceOptions
): Promise<
  NonNullablePaths<
    GetInvoiceResponse,
    | `invoice.items`
    | `invoice.items.${number}._id`
    | `invoice.items.${number}.quantity`
    | `invoice.items.${number}.name`
    | `invoice.items.${number}.price.amount`
    | `invoice.items.${number}.price.currency`
    | `invoice.items.${number}.discount.code`
    | `invoice.items.${number}.discount.name`
    | `invoice.items.${number}.discount.couponId`
    | `invoice.items.${number}.tax.type`
    | `invoice.items.${number}.tax.name`
    | `invoice.items.${number}.tax.rate`
    | `invoice.fees`
    | `discountErrors.error`
    | `discountErrors.error.${number}.code`
    | `reservationStatus`
    | `reservationOccupied`
    | `reservations`
    | `reservations.${number}.quantity`
    | `reservations.${number}.ticket._id`
    | `reservations.${number}.ticket.price.amount`
    | `reservations.${number}.ticket.price.currency`
    | `reservations.${number}.ticket.free`
    | `reservations.${number}.ticket.name`
    | `reservations.${number}.ticket.description`
    | `reservations.${number}.ticket.limitPerCheckout`
    | `reservations.${number}.ticket.orderIndex`
    | `reservations.${number}.ticket.policy`
    | `reservations.${number}.ticket.dashboard.hidden`
    | `reservations.${number}.ticket.dashboard.sold`
    | `reservations.${number}.ticket.dashboard.limited`
    | `reservations.${number}.ticket.dashboard.ticketsSold`
    | `reservations.${number}.ticket.dashboard.ticketsReserved`
    | `reservations.${number}.ticket.eventId`
    | `reservations.${number}.ticket.wixFeeConfig.type`
    | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
    | `reservations.${number}.ticket.saleStatus`
    | `reservations.${number}.ticket.pricing.pricingType`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    reservationId: reservationId,
    eventId: eventId,
    withDiscount: options?.withDiscount,
    paidPlanBenefit: options?.paidPlanBenefit,
  });

  const reqOpts = ambassadorWixEventsV1Order.getInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          reservationId: '$[0]',
          eventId: '$[1]',
          withDiscount: '$[2].withDiscount',
          paidPlanBenefit: '$[2].paidPlanBenefit',
        },
        singleArgumentUnchanged: false,
      },
      ['reservationId', 'eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetInvoiceOptions {
  /** Optional discount to be applied on the returned invoice. */
  withDiscount?: DiscountRequest;
  /** Optional benefit granted by the pricing plan to be applied on the returned invoice. */
  paidPlanBenefit?: PaidPlanBenefit;
}

/**
 * Checkouts the reserved tickets.
 *
 *
 * Creates an order and associates it with a site visitor contact.
 * Guest details are received from the [Registration Form](https://www.wix.com/velo/reference/wix-events-v2/forms/introduction) input fields.
 *
 * There is a possibility to use a separate ready-made Wix checkout form where the user will be redirected from your non-Wix site or a custom ticket picker created with Velo.
 * To build the checkout form path, get your event base URL by using the [`getEvent()`](https://www.wix.com/velo/reference/wix-events-backend/wixevents/getevent) function and add the following path:
 * `/{{EVENT_PAGE_SLUG}}/{{SLUG}}/ticket-form?reservationId={{YOUR_RESERVATION_ID}}`
 *
 * Example:  `https://johndoe.wixsite.com/weddings/event-details/doe-wedding/ticket-form?reservationId=2be6d34a-2a1e-459f-897b-b4a66e73f69a`
 * @public
 * @requiredField eventId
 * @requiredField options.guests.form
 * @param options - An object representing the available options for checking out a reserved ticket.
 * @param eventId - Event ID to which the checkout belongs.
 * @permissionId WIX_EVENTS.CHECKOUT
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.Checkout
 */
export async function checkout(
  eventId: string,
  options?: NonNullablePaths<
    CheckoutOptionsForRequest,
    `guests.${number}.form`,
    4
  >
): Promise<
  NonNullablePaths<
    CheckoutResponse,
    | `order.orderNumber`
    | `order.reservationId`
    | `order.snapshotId`
    | `order.eventId`
    | `order.contactId`
    | `order.memberId`
    | `order.firstName`
    | `order.lastName`
    | `order.email`
    | `order.checkoutForm.inputValues`
    | `order.checkoutForm.inputValues.${number}.inputName`
    | `order.checkoutForm.inputValues.${number}.value`
    | `order.confirmed`
    | `order.status`
    | `order.method`
    | `order.ticketsQuantity`
    | `order.totalPrice.amount`
    | `order.totalPrice.currency`
    | `order.ticketsPdf`
    | `order.tickets`
    | `order.tickets.${number}.ticketNumber`
    | `order.tickets.${number}.orderNumber`
    | `order.tickets.${number}.ticketDefinitionId`
    | `order.tickets.${number}.name`
    | `order.tickets.${number}.free`
    | `order.tickets.${number}.policy`
    | `order.tickets.${number}.orderStatus`
    | `order.tickets.${number}.orderArchived`
    | `order.tickets.${number}.orderFullName`
    | `order.tickets.${number}.guestDetails.guestAssigned`
    | `order.tickets.${number}.archived`
    | `order.tickets.${number}.anonymized`
    | `order.tickets.${number}.checkInUrl`
    | `order.tickets.${number}.ticketPdfUrl`
    | `order.tickets.${number}.channel`
    | `order.tickets.${number}.walletPassUrl`
    | `order.archived`
    | `order.anonymized`
    | `order.fullName`
    | `order.invoice.items`
    | `order.invoice.items.${number}._id`
    | `order.invoice.items.${number}.quantity`
    | `order.invoice.items.${number}.name`
    | `order.invoice.items.${number}.discount.code`
    | `order.invoice.items.${number}.discount.name`
    | `order.invoice.items.${number}.discount.couponId`
    | `order.invoice.items.${number}.tax.type`
    | `order.invoice.items.${number}.tax.name`
    | `order.invoice.items.${number}.tax.rate`
    | `order.invoice.fees`
    | `order.fullyCheckedIn`
    | `order.paymentDetails.transaction.transactionId`
    | `order.paymentDetails.transaction.method`
    | `order.channel`
    | `reservations`
    | `reservations.${number}.quantity`
    | `reservations.${number}.ticket._id`
    | `reservations.${number}.ticket.price.amount`
    | `reservations.${number}.ticket.price.currency`
    | `reservations.${number}.ticket.free`
    | `reservations.${number}.ticket.name`
    | `reservations.${number}.ticket.description`
    | `reservations.${number}.ticket.limitPerCheckout`
    | `reservations.${number}.ticket.orderIndex`
    | `reservations.${number}.ticket.policy`
    | `reservations.${number}.ticket.dashboard.hidden`
    | `reservations.${number}.ticket.dashboard.sold`
    | `reservations.${number}.ticket.dashboard.limited`
    | `reservations.${number}.ticket.dashboard.ticketsSold`
    | `reservations.${number}.ticket.dashboard.ticketsReserved`
    | `reservations.${number}.ticket.eventId`
    | `reservations.${number}.ticket.wixFeeConfig.type`
    | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
    | `reservations.${number}.ticket.saleStatus`
    | `reservations.${number}.ticket.pricing.pricingType`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      eventId: eventId,
      reservationId: options?.reservationId,
      memberId: options?.memberId,
      discount: options?.discount,
      buyer: options?.buyer,
      guests: options?.guests,
      paidPlanBenefit: options?.paidPlanBenefit,
      options: options?.options,
      marketingConsent: options?.marketingConsent,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'guests.form.inputValues.address.address' }],
      },
    ]
  );

  const reqOpts = ambassadorWixEventsV1Order.checkout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'order.checkoutForm.inputValues.address.address' },
            {
              path: 'order.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          reservationId: '$[1].reservationId',
          memberId: '$[1].memberId',
          discount: '$[1].discount',
          buyer: '$[1].buyer',
          guests: '$[1].guests',
          paidPlanBenefit: '$[1].paidPlanBenefit',
          options: '$[1].options',
          marketingConsent: '$[1].marketingConsent',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CheckoutOptionsForRequest {
  /**
   * Ticket reservation ID.
   * @format GUID
   */
  reservationId?: string;
  /** Member ID. If empty, no site member is associated with this order. */
  memberId?: string;
  /** Discount to apply on the invoice. */
  discount?: DiscountRequest;
  /** Buyer details. */
  buyer?: Buyer;
  /**
   * Guest details.
   * @minSize 1
   * @maxSize 50
   */
  guests?: Guest[];
  /** Benefit granted by the pricing plan. */
  paidPlanBenefit?: PaidPlanBenefit;
  /** Options controlling the checkout process. */
  options?: CheckoutOptions;
  /** Whether marketing consent was given. */
  marketingConsent?: boolean | null;
}

/**
 * Updates order and tickets.
 *
 *
 * Only applicable for orders with `INITIATED`, `PENDING`, `OFFLINE_PENDING` statuses.
 * @param orderNumber - Unique order number.
 * @public
 * @requiredField eventId
 * @requiredField orderNumber
 * @param options - An object representing the available options for updating an order and tickets.
 * @param identifiers - An object containing identifiers for the order and tickets to be updated.
 * @param eventId - Event ID to which the checkout belongs.
 * @permissionId WIX_EVENTS.CHECKOUT
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.UpdateCheckout
 */
export async function updateCheckout(
  orderNumber: string,
  eventId: string,
  options?: UpdateCheckoutOptions
): Promise<
  NonNullablePaths<
    UpdateCheckoutResponse,
    | `order.orderNumber`
    | `order.reservationId`
    | `order.snapshotId`
    | `order.eventId`
    | `order.contactId`
    | `order.memberId`
    | `order.firstName`
    | `order.lastName`
    | `order.email`
    | `order.checkoutForm.inputValues`
    | `order.checkoutForm.inputValues.${number}.inputName`
    | `order.checkoutForm.inputValues.${number}.value`
    | `order.confirmed`
    | `order.status`
    | `order.method`
    | `order.ticketsQuantity`
    | `order.totalPrice.amount`
    | `order.totalPrice.currency`
    | `order.ticketsPdf`
    | `order.tickets`
    | `order.tickets.${number}.ticketNumber`
    | `order.tickets.${number}.orderNumber`
    | `order.tickets.${number}.ticketDefinitionId`
    | `order.tickets.${number}.name`
    | `order.tickets.${number}.free`
    | `order.tickets.${number}.policy`
    | `order.tickets.${number}.orderStatus`
    | `order.tickets.${number}.orderArchived`
    | `order.tickets.${number}.orderFullName`
    | `order.tickets.${number}.guestDetails.guestAssigned`
    | `order.tickets.${number}.archived`
    | `order.tickets.${number}.anonymized`
    | `order.tickets.${number}.checkInUrl`
    | `order.tickets.${number}.ticketPdfUrl`
    | `order.tickets.${number}.channel`
    | `order.tickets.${number}.walletPassUrl`
    | `order.archived`
    | `order.anonymized`
    | `order.fullName`
    | `order.invoice.items`
    | `order.invoice.items.${number}._id`
    | `order.invoice.items.${number}.quantity`
    | `order.invoice.items.${number}.name`
    | `order.invoice.items.${number}.discount.code`
    | `order.invoice.items.${number}.discount.name`
    | `order.invoice.items.${number}.discount.couponId`
    | `order.invoice.items.${number}.tax.type`
    | `order.invoice.items.${number}.tax.name`
    | `order.invoice.items.${number}.tax.rate`
    | `order.invoice.fees`
    | `order.fullyCheckedIn`
    | `order.paymentDetails.transaction.transactionId`
    | `order.paymentDetails.transaction.method`
    | `order.channel`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      orderNumber: orderNumber,
      eventId: eventId,
      buyer: options?.buyer,
      guests: options?.guests,
      memberId: options?.memberId,
      discount: options?.discount,
      paidPlanBenefit: options?.paidPlanBenefit,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'guests.form.inputValues.address.address' }],
      },
    ]
  );

  const reqOpts = ambassadorWixEventsV1Order.updateCheckout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'order.checkoutForm.inputValues.address.address' },
            {
              path: 'order.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          orderNumber: '$[0]',
          eventId: '$[1]',
          buyer: '$[2].buyer',
          guests: '$[2].guests',
          memberId: '$[2].memberId',
          discount: '$[2].discount',
          paidPlanBenefit: '$[2].paidPlanBenefit',
        },
        singleArgumentUnchanged: false,
      },
      ['orderNumber', 'eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCheckoutOptions {
  /** Buyer details. */
  buyer?: Buyer;
  /**
   * Guest details.
   * @minSize 1
   * @maxSize 20
   */
  guests?: Guest[];
  /**
   * Member ID.
   *
   * If empty, no site member is associated with this order.
   */
  memberId?: string | null;
  /** Discount to apply on the invoice. */
  discount?: DiscountRequest;
  /** Benefit granted by the pricing plan. */
  paidPlanBenefit?: PaidPlanBenefit;
}

/**
 * Creates order with payment details already initiated via Cashier Pay API.
 * @public
 * @documentationMaturity preview
 * @requiredField eventId
 * @requiredField options.reservationId
 * @param eventId - Event ID to which the checkout belongs.
 * @permissionId WIX_EVENTS.MANAGE_ORDERS
 * @applicableIdentity APP
 * @fqn wix.events.ticketing.CheckoutService.PosCheckout
 */
export async function posCheckout(
  eventId: string,
  options?: NonNullablePaths<PosCheckoutOptions, `reservationId`, 2>
): Promise<
  NonNullablePaths<
    PosCheckoutResponse,
    | `order.orderNumber`
    | `order.reservationId`
    | `order.snapshotId`
    | `order.eventId`
    | `order.contactId`
    | `order.memberId`
    | `order.firstName`
    | `order.lastName`
    | `order.email`
    | `order.checkoutForm.inputValues`
    | `order.checkoutForm.inputValues.${number}.inputName`
    | `order.checkoutForm.inputValues.${number}.value`
    | `order.confirmed`
    | `order.status`
    | `order.method`
    | `order.ticketsQuantity`
    | `order.totalPrice.amount`
    | `order.totalPrice.currency`
    | `order.ticketsPdf`
    | `order.tickets`
    | `order.tickets.${number}.ticketNumber`
    | `order.tickets.${number}.orderNumber`
    | `order.tickets.${number}.ticketDefinitionId`
    | `order.tickets.${number}.name`
    | `order.tickets.${number}.free`
    | `order.tickets.${number}.policy`
    | `order.tickets.${number}.orderStatus`
    | `order.tickets.${number}.orderArchived`
    | `order.tickets.${number}.orderFullName`
    | `order.tickets.${number}.guestDetails.guestAssigned`
    | `order.tickets.${number}.archived`
    | `order.tickets.${number}.anonymized`
    | `order.tickets.${number}.checkInUrl`
    | `order.tickets.${number}.ticketPdfUrl`
    | `order.tickets.${number}.channel`
    | `order.tickets.${number}.walletPassUrl`
    | `order.archived`
    | `order.anonymized`
    | `order.fullName`
    | `order.invoice.items`
    | `order.invoice.items.${number}._id`
    | `order.invoice.items.${number}.quantity`
    | `order.invoice.items.${number}.name`
    | `order.invoice.items.${number}.discount.code`
    | `order.invoice.items.${number}.discount.name`
    | `order.invoice.items.${number}.discount.couponId`
    | `order.invoice.items.${number}.tax.type`
    | `order.invoice.items.${number}.tax.name`
    | `order.invoice.items.${number}.tax.rate`
    | `order.invoice.fees`
    | `order.fullyCheckedIn`
    | `order.paymentDetails.transaction.transactionId`
    | `order.paymentDetails.transaction.method`
    | `order.channel`
    | `reservations`
    | `reservations.${number}.quantity`
    | `reservations.${number}.ticket._id`
    | `reservations.${number}.ticket.price.amount`
    | `reservations.${number}.ticket.price.currency`
    | `reservations.${number}.ticket.free`
    | `reservations.${number}.ticket.name`
    | `reservations.${number}.ticket.description`
    | `reservations.${number}.ticket.limitPerCheckout`
    | `reservations.${number}.ticket.orderIndex`
    | `reservations.${number}.ticket.policy`
    | `reservations.${number}.ticket.dashboard.hidden`
    | `reservations.${number}.ticket.dashboard.sold`
    | `reservations.${number}.ticket.dashboard.limited`
    | `reservations.${number}.ticket.dashboard.ticketsSold`
    | `reservations.${number}.ticket.dashboard.ticketsReserved`
    | `reservations.${number}.ticket.eventId`
    | `reservations.${number}.ticket.wixFeeConfig.type`
    | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
    | `reservations.${number}.ticket.saleStatus`
    | `reservations.${number}.ticket.pricing.pricingType`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    reservationId: options?.reservationId,
    paymentDetailsId: options?.paymentDetailsId,
  });

  const reqOpts = ambassadorWixEventsV1Order.posCheckout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'order.checkoutForm.inputValues.address.address' },
            {
              path: 'order.tickets.guestDetails.form.inputValues.address.address',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          reservationId: '$[1].reservationId',
          paymentDetailsId: '$[1].paymentDetailsId',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PosCheckoutOptions {
  /**
   * Ticket reservation ID.
   * @format GUID
   */
  reservationId: string;
  /**
   * Payment details ID.
   * Not required if reservation total is 0. In this case the order will be created with status `FREE` and no payment.
   * @format GUID
   */
  paymentDetailsId?: string | null;
}
